import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { v2Api, v2 } from "@/lib/v2Api";
import {
  FileText,
  ChevronLeft,
  Clock,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Download,
  MessageSquare,
  Loader2,
  Calendar,
  Building2,
  Scale,
  Briefcase,
  Wallet,
  ClipboardList,
  RefreshCw,
  Trash2,
  Wand2,
  Eye,
  ChevronDown,
  ChevronUp,
  Image,
  BookOpen,
  Shield,

  X,
  Flag,
  BarChart3,
  Sparkles,
  ListChecks,
  XCircle,
  Plus,
  ExternalLink,
  Minus,
  Pencil,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import ProductTagger from "../components/ProductTagger";
import SectionReviewBar from "@/components/v2/SectionReviewBar";
import RefreshButton from "@/components/v2/RefreshButton";
import SectionOriginalReader from "@/components/v2/SectionOriginalReader";
import SectionPdfPane from "@/components/v2/SectionPdfPane";
import ManualSectionsPanel from "@/components/v2/ManualSectionsPanel";
import SectionExemplarsSheet from "@/components/v2/SectionExemplarsSheet";
import DiagramsPanel from "@/components/v2/DiagramsPanel";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import SectionCrossValidationSheet from "@/components/v2/SectionCrossValidationSheet";
import SectionExplainer from "@/components/v2/SectionExplainer";
import { useRfpOptional } from "@/contexts/RfpContext";
import { useAuth } from "@/contexts/AuthContext";
import RfpBoqTab from "@/components/v2/RfpBoqTab";
import GlossaryHover from "@/components/v2/GlossaryHover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MissingTemplateChip — clickable pill for one missing required
 * template. Opens a popover showing what the classifier looked for
 * and shortcuts to the three recovery paths:
 *   - Edit the template (refine its keywords / description)
 *   - Add a manual section to satisfy the requirement
 *   - Re-classify (cheap, no Gemini) after editing the taxonomy
 *
 * meta shape (from rfp_pipeline.summary.missing_defaults):
 *   { code, name, tier }
 */
function MissingTemplateChip({ meta }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 transition"
          title={`Click for details and recovery options · template code: ${meta.code}`}
        >
          {meta.name || meta.code}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 text-sm" align="start">
        <div className="space-y-2">
          <div>
            <p className="font-semibold text-slate-800">{meta.name || meta.code}</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">
              <span className="font-mono">{meta.code}</span> · {meta.tier} tier
            </p>
          </div>
          <p className="text-xs text-slate-600">
            The classifier didn't stamp this template's code on any section
            of this RFP. Pick one of:
          </p>
          <div className="space-y-1.5">
            <Link
              to="/settings?tab=templates"
              className="block text-xs px-2 py-1 rounded bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200"
            >
              Edit this template's keywords / description →
            </Link>
            <Link
              to="#manual-sections"
              className="block text-xs px-2 py-1 rounded bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200"
            >
              Scroll to "Manual sections" to add one →
            </Link>
            <p className="text-[11px] text-slate-500 pt-1 border-t border-slate-100">
              Tip: refine the template, then hit the channel header's
              <span className="font-medium"> Reclassify </span>
              button — no Gemini tokens spent.
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ReconciledNumbersCard({ reconciled }) {
  if (!reconciled) return null;
  const entries = Object.entries(reconciled);
  if (!entries.length) return null;
  return (
    <Card className="border-[#E2E8F0]">
      <CardHeader className="pb-3">
        <CardTitle className="font-['Manrope'] text-lg font-bold">Key Figures</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {entries.map(([key, val]) => (
          <div key={key} className="flex justify-between items-start gap-2">
            <span className="text-sm text-[#64748B] capitalize">{key.replace(/_/g, " ")}</span>
            <div className="text-right">
              <span className="font-semibold text-sm">{val?.canonical ?? "—"}</span>
              {val?.conflicting && (
                <span className="ml-2 text-xs text-amber-600 font-medium">conflict</span>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

const SECTION_TAG_TYPES = [
  { type: "requirement", label: "Requirement", activeCls: "bg-indigo-100 text-indigo-700 border-indigo-300", icon: <CheckCircle2 className="w-3 h-3" /> },
  { type: "compliance",  label: "Compliance",  activeCls: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: <Shield className="w-3 h-3" /> },
  { type: "deadline",    label: "Deadline",    activeCls: "bg-red-100 text-red-700 border-red-300",    icon: <Calendar className="w-3 h-3" /> },
  { type: "boq_note",   label: "BOQ",         activeCls: "bg-blue-100 text-blue-700 border-blue-300",  icon: <ClipboardList className="w-3 h-3" /> },
];

// Simple 4-chip tag toggle — tags drive display in Requirements / Compliance /
// Deadlines / BOQ tabs. allLinks is the RFP-level link state; setAllLinks
// lets the toggle update the shared store so tabs refresh instantly.
function SectionLinksRow({ rfpId, sectionIndex, allLinks, setAllLinks }) {
  const [busy, setBusy] = React.useState({});

  const links = allLinks.filter((l) => l.section_index === sectionIndex);
  const activeTypes = new Set(links.filter((l) => !l.entity_id && !l.note_text).map((l) => l.entity_type));

  const handleToggle = async (tagType) => {
    const existing = links.find((l) => l.entity_type === tagType && !l.entity_id && !l.note_text);
    setBusy((b) => ({ ...b, [tagType]: true }));
    try {
      if (existing) {
        await v2Api.briefItems.deleteSectionLink(existing.id);
        setAllLinks((prev) => prev.filter((l) => l.id !== existing.id));
      } else {
        const lnk = await v2Api.briefItems.createSectionLink(rfpId, {
          section_index: sectionIndex,
          entity_type: tagType,
        });
        setAllLinks((prev) => [...prev, lnk]);
      }
    } catch {
      toast.error("Could not update tag");
    } finally {
      setBusy((b) => ({ ...b, [tagType]: false }));
    }
  };

  return (
    <div className="mb-3 flex items-center gap-1.5 flex-wrap">
      <span className="text-xs text-[#64748B] font-medium mr-0.5">Tags:</span>
      {SECTION_TAG_TYPES.map(({ type, label, activeCls, icon }) => {
        const active = activeTypes.has(type);
        return (
          <button
            key={type}
            type="button"
            disabled={busy[type]}
            onClick={() => handleToggle(type)}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border font-medium transition-colors ${
              active
                ? activeCls
                : "border-dashed border-slate-300 text-slate-400 hover:border-slate-400 hover:text-slate-600"
            }`}
          >
            {busy[type] ? <Loader2 className="w-3 h-3 animate-spin" /> : icon}
            {label}
          </button>
        );
      })}
    </div>
  );
}

// Shows all sections tagged with a specific type — used by Requirements,
// Compliance, Deadlines, and BOQ tabs to surface reviewer-flagged sections.
function TaggedSectionsPanel({ tagType, allLinks, sections, onGoToSection }) {
  const taggedIndices = new Set(
    allLinks
      .filter((l) => l.entity_type === tagType && !l.entity_id && !l.note_text)
      .map((l) => l.section_index)
  );

  if (taggedIndices.size === 0) return null;

  const taggedSections = sections.filter((s) => taggedIndices.has(s.section_index ?? s.index));
  if (taggedSections.length === 0) return null;

  const { label, activeCls, icon } = SECTION_TAG_TYPES.find((t) => t.type === tagType);

  return (
    <Card className="border-[#E2E8F0]">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border font-medium ${activeCls}`}>
            {icon} {label}
          </span>
          Sections tagged by reviewers
          <Badge variant="outline" className="text-[10px] font-normal">{taggedSections.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {taggedSections.map((s) => {
            const idx = s.section_index ?? s.index;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => onGoToSection?.(s)}
                className="w-full text-left flex items-start gap-3 p-3 rounded-lg border border-[#E2E8F0] bg-slate-50 hover:bg-slate-100 transition-colors group"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 group-hover:text-indigo-700 truncate">
                    {s.title}
                  </p>
                  {s.summary && (
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{s.summary}</p>
                  )}
                </div>
                {s.page_start != null && (
                  <span className="shrink-0 text-[10px] text-slate-400 mt-0.5">p.{s.page_start + 1}</span>
                )}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

const RFPAnalysis = () => {
  const { rfpId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const userIsAdmin = isAdmin();
  // A6: when rendered inside TenderWorkspace, the RFP row has already
  // been fetched by RfpProvider — reuse it and skip the duplicate GET.
  // When rendered standalone (legacy route), the context is null and
  // we fetch as before.
  const rfpContext = useRfpOptional();
  const sectionReviews = rfpContext?.sectionReviews || {};
  const [rfp, setRfp] = useState(rfpContext?.rfp || null);
  // file_type is not in RfpOut — derive it from file_path extension.
  const isPdfRfp = rfp?.file_path?.toLowerCase().endsWith(".pdf") ?? false;
  const [sections, setSections] = useState([]);
  const [sectionsSummary, setSectionsSummary] = useState(null);
  const [regeneratingBrief, setRegeneratingBrief] = useState(false);
  const [regeneratingExec, setRegeneratingExec] = useState(false);
  const [regeneratingCompliance, setRegeneratingCompliance] = useState(false);
  const [regeneratingTables, setRegeneratingTables] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [activeDepartment, setActiveDepartment] = useState("all");
  const [exporting, setExporting] = useState(false);
  
  // Original content viewer state - inline instead of modal
  const [expandedSections, setExpandedSections] = useState({});
  const [sectionPageImages, setSectionPageImages] = useState({}); // pageImages per section
  const [loadingPageImages, setLoadingPageImages] = useState({});

  // Priority #3: slide-over PDF reader for one section's page range.
  // Driven by ``readerSection`` — the v1 section row whose "View
  // original ↗" button was clicked. Null when closed.
  const [readerSection, setReaderSection] = useState(null);

  // "View on PDF" dialog: null when closed, section object when open.
  const [pdfDialogSection, setPdfDialogSection] = useState(null);

  // Priority #5: per-section Library exemplars. ``exemplarsSection``
  // is the section row whose body should drive the Library search.
  const [exemplarsSection, setExemplarsSection] = useState(null);

  // Priority #6: section ↔ product cross-validation. Holds the
  // adapted section row whose section_index drives the backend call.
  const [crossValSection, setCrossValSection] = useState(null);

  // Brief-items: review progress + per-item review state
  const [reviewProgress, setReviewProgress] = useState(null);
  const [requirementItems, setRequirementItems] = useState([]);
  const [complianceItems, setComplianceItems] = useState([]);
  const [reqItemsBusy, setReqItemsBusy] = useState({});
  const [compItemsBusy, setCompItemsBusy] = useState({});
  // itemId → { note, pendingStatus } for inline note editing
  const [reqNoteState, setReqNoteState] = useState({});
  const [compNoteState, setCompNoteState] = useState({});
  // itemId → { text } for inline item text editing
  const [reqEditState, setReqEditState] = useState({});
  const [compEditState, setCompEditState] = useState({});
  // deadlineIndex → { date, context, saving } for inline deadline editing
  const [deadlineEditState, setDeadlineEditState] = useState({});
  const [showIgnoredDeadlines, setShowIgnoredDeadlines] = useState(false);
  // category → { open, text, saving } for inline add-item forms
  const [addItemState, setAddItemState] = useState({});
  const [addCompState, setAddCompState] = useState({});
  const [addDeadlineState, setAddDeadlineState] = useState({ open: false, date: "", context: "", saving: false });
  const [reclassifying, setReclassifying] = useState(false);
  // All section-tag links for this RFP — shared between SectionLinksRow
  // (for toggle state) and the tagged-section panels in each tab.
  const [allSectionLinks, setAllSectionLinks] = useState([]);

  // Scroll-to + flash the section matching the SearchPanel's last
  // focused hit. v1 section IDs don't line up with v2 section_index, so
  // we match by title (case-insensitive, substring tolerant). The
  // focusToken changes on every click so re-clicking the same hit
  // re-runs the effect.
  useEffect(() => {
    if (!rfpContext || rfpContext.focusToken === 0) return;
    const wantedTitle = (rfpContext.focusSectionTitle || "").trim().toLowerCase();
    if (!wantedTitle) return;
    if (activeDepartment !== "all") setActiveDepartment("all");
    // Wait a tick so the tab switch + filter reset has rendered.
    const t = setTimeout(() => {
      const target = sections.find((s) =>
        (s.title || "").toLowerCase().includes(wantedTitle) ||
        wantedTitle.includes((s.title || "").toLowerCase())
      );
      if (!target) return;
      setExpandedSections((prev) => ({ ...prev, [target.id]: true }));
      const node = document.querySelector(
        `[data-testid="section-${target.id}"]`
      );
      if (node) {
        node.scrollIntoView({ behavior: "smooth", block: "start" });
        node.classList.add("ring-2", "ring-blue-400", "ring-offset-2");
        setTimeout(() => {
          node.classList.remove("ring-2", "ring-blue-400", "ring-offset-2");
        }, 1800);
      }
    }, 80);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfpContext?.focusToken]);

  const departmentConfig = {
    all: { label: "All Sections", icon: FileText, color: "text-[#007AFF]" },
    Technical: { label: "Technical", icon: Briefcase, color: "text-indigo-600" },
    Finance: { label: "Finance", icon: Wallet, color: "text-emerald-600" },
    Legal: { label: "Legal", icon: Scale, color: "text-red-600" },
    Operations: { label: "Operations", icon: Building2, color: "text-amber-600" },
  };

  const sectionTypeIcons = {
    technical: Briefcase,
    financial: Wallet,
    legal: Scale,
    scope: ClipboardList,
    eligibility: CheckCircle2,
    payment_terms: Wallet,
    other: FileText
  };

  // Map a v2 SectionOut into the shape this page's downstream JSX
  // expects (id, section_type, content, ...). v1 Mongo and v2
  // Postgres carry different fields; rather than rewrite every
  // section-render JSX path, we adapt at the boundary so v2-only
  // tenders (the majority now) render exactly like v1 did.
  // v2 GET /rfp/{id} returns ``pages``, ``analysis_summary`` (a nested
  // dict containing executive, deadlines, categories, etc). The Brief
  // tab JSX still uses the v1 field names (total_pages, overall_summary,
  // key_deadlines, compliance_status.total_items) — adapt at the
  // boundary so the JSX is untouched and v2-only tenders render.
  const adaptV2Rfp = (raw) => {
    if (!raw) return raw;
    const summary = raw.analysis_summary || {};
    const deadlines = Array.isArray(summary.deadlines)
      ? summary.deadlines.map((d) => ({
          event: d.event,
          // JSX renders ``deadline.context`` (legacy v1 field name);
          // populate both so the existing block displays the actual
          // event description next to the date instead of a bare date.
          context: d.event || "",
          date: d.date,
          page: d._citation?.page,
        }))
      : raw.key_deadlines || [];
    const complianceTotal = Array.isArray(summary.categories)
      ? summary.categories.reduce((sum, c) => sum + (c.field_count || 0), 0)
      : raw.compliance_status?.total_items || 0;
    return {
      ...raw,
      total_pages: raw.pages || raw.total_pages || 0,
      overall_summary: summary.executive || raw.overall_summary || "",
      key_deadlines: deadlines,
      compliance_status: {
        ...(raw.compliance_status || {}),
        total_items: complianceTotal,
      },
      // Phase B: structured requirements brief surfaces on the
      // "Requirements" tab. Always-defined dict so the tab renders
      // placeholders even on legacy analyses that lack the field.
      requirements_brief: summary.requirements_brief || {},
      // Structured tables extracted by the tables_s pipeline stage:
      // [{title, kind, headers, rows}]. Empty array on legacy analyses
      // — the Tables tab shows a friendly placeholder in that case.
      tables: Array.isArray(summary.tables) ? summary.tables : [],
    };
  };

  const adaptV2Section = (v2sec) => ({
    id: String(v2sec.section_index),
    section_index: v2sec.section_index,
    title: v2sec.title,
    section_type: (v2sec.department || "other").toLowerCase().replace(/\s+/g, "_"),
    content: v2sec.body || v2sec.body_preview || "",
    // Per-section AI summary populated by rfp_pipeline's
    // section_summary_s stage when use_llm=True. Null on older
    // analyses or when summarize_section returned None — the render
    // path at line ~687 hides the Summary block in that case.
    summary: v2sec.summary || null,
    page_start: v2sec.page_start || 0,
    page_end: v2sec.page_end || 0,
    department: v2sec.department,
    department_id: v2sec.department_id || null,
    template_code: v2sec.template_code,
    template_id: v2sec.template_id,
    body_truncated: !!v2sec.body_truncated,
    body_full_length: v2sec.body_full_length ?? null,
    // v1 had compliance_items inline; v2 doesn't ship them on the
    // section payload. The Compliance tab gracefully renders empty.
    compliance_items: [],
    product_tags: v2sec.product_tags || [],
  });

  const fetchRFPData = async () => {
    // Clear stale state immediately so the previous RFP's data
    // (executive summary, sections, etc.) doesn't show while loading.
    setRfp(null);
    setSections([]);
    setSectionsSummary(null);
    setLoading(true);
    try {
      // v2 endpoints. Item 3c stage 1 (c4ea88e) shipped
      // /api/v2/rfp/{id}/sections and /sections/summary; we never
      // swapped this code path off the v1 Mongo shadow, so any RFP
      // analysed via the v2 pipeline showed "0 sections" in the
      // Brief tab forever.
      //
      // Initial load is body-LESS: for a 100+ section tender the
      // full-body payload tops 20 MB and blew through the axios
      // timeout. Titles + summaries + page ranges + body_preview are
      // enough to render the section list. The "View original" toggle
      // can re-fetch a single section's body on demand.
      const sectionsPromise = v2Api.rfp.sections(rfpId, { includeBody: false });
      const summaryPromise = v2Api.rfp.sectionsSummary(rfpId);
      // Always fetch fresh from API — never reuse rfpContext.rfp
      // across RFP navigations, as it may still hold the previous RFP.
      const rfpPromise = v2Api.rfp.get(rfpId);
      const [rfpData, sectionsData, summaryData] = await Promise.all([
        rfpPromise, sectionsPromise, summaryPromise,
      ]);

      setRfp(adaptV2Rfp(rfpData));
      setSections((Array.isArray(sectionsData) ? sectionsData : []).map(adaptV2Section));
      setSectionsSummary(summaryData);
    } catch (error) {
      console.error("Error fetching RFP:", error);
      if (error.response?.status === 404) {
        toast.error("RFP not found");
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRFPData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfpId]);

  // Load brief-items data (requirement items, compliance items, review
  // progress) once the RFP is available.
  const loadBriefItemsData = useCallback(async () => {
    if (!rfpId) return;
    try {
      const [progress, reqItems, compItems] = await Promise.all([
        v2Api.briefItems.reviewProgress(rfpId),
        v2Api.briefItems.requirementItems(rfpId),
        v2Api.briefItems.complianceItems(rfpId),
      ]);
      setReviewProgress(progress);
      setRequirementItems(reqItems || []);
      setComplianceItems(compItems || []);
    } catch (err) {
      // Non-fatal — page still works, review strips just don't show
      console.warn("brief items load failed:", err?.message);
    }
  }, [rfpId]);

  useEffect(() => {
    loadBriefItemsData();
  }, [loadBriefItemsData]);

  // Load all section-tag links for the whole RFP (shared state powering
  // SectionLinksRow toggles + tagged-section panels in the 4 review tabs).
  useEffect(() => {
    if (!rfpId) return;
    v2Api.briefItems.sectionLinks(rfpId)
      .then((data) => setAllSectionLinks(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [rfpId]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.post(
        `${API}/rfp/${rfpId}/export`,
        { rfp_id: rfpId, export_type: "pdf" },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `RFP_Report_${rfp?.name || 'export'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Report exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export report");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/rfp/${rfpId}`);
      toast.success("RFP deleted successfully");
      navigate("/");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete RFP");
    }
  };

  const handleSetReqStatus = async (item, nextStatus, note) => {
    setReqItemsBusy((p) => ({ ...p, [item.id]: true }));
    try {
      const payload = { review_status: nextStatus };
      if (note !== undefined) payload.review_note = note;
      const updated = await v2Api.briefItems.patchRequirementItem(item.id, payload);
      setRequirementItems((prev) => prev.map((r) => r.id === item.id ? { ...r, ...updated } : r));
      setReqNoteState((p) => { const n = { ...p }; delete n[item.id]; return n; });
      loadBriefItemsData();
    } catch {
      toast.error("Could not update requirement item");
    } finally {
      setReqItemsBusy((p) => ({ ...p, [item.id]: false }));
    }
  };

  const handleAddReqItem = async (category, text) => {
    if (!text.trim()) return;
    setAddItemState((p) => ({ ...p, [category]: { ...p[category], saving: true } }));
    try {
      const created = await v2Api.briefItems.createRequirementItem(rfpId, { category, text: text.trim() });
      setRequirementItems((prev) => [...prev, { ...created, review_status: "pending", review_note: null, reviewer_name: null, reviewed_at: null, section_index: null }]);
      setAddItemState((p) => ({ ...p, [category]: { open: false, text: "", saving: false } }));
    } catch {
      toast.error("Could not add requirement");
      setAddItemState((p) => ({ ...p, [category]: { ...p[category], saving: false } }));
    }
  };

  const handleSetCompStatus = async (item, nextStatus, note) => {
    setCompItemsBusy((p) => ({ ...p, [item.id]: true }));
    try {
      const payload = { review_status: nextStatus };
      if (note !== undefined) payload.review_note = note;
      const updated = await v2Api.briefItems.patchComplianceItem(item.id, payload);
      setComplianceItems((prev) => prev.map((r) => r.id === item.id ? { ...r, ...updated } : r));
      setCompNoteState((p) => { const n = { ...p }; delete n[item.id]; return n; });
      loadBriefItemsData();
    } catch {
      toast.error("Could not update compliance item");
    } finally {
      setCompItemsBusy((p) => ({ ...p, [item.id]: false }));
    }
  };

  const handleAddCompItem = async (category, text) => {
    if (!text.trim()) return;
    setAddCompState((p) => ({ ...p, [category]: { ...p[category], saving: true } }));
    try {
      const created = await v2Api.briefItems.createComplianceItem(rfpId, { category, text: text.trim() });
      setComplianceItems((prev) => [...prev, { ...created, review_status: "pending", review_note: null, reviewer_name: null, reviewed_at: null, section_index: null }]);
      setAddCompState((p) => ({ ...p, [category]: { open: false, text: "", saving: false } }));
    } catch {
      toast.error("Could not add compliance item");
      setAddCompState((p) => ({ ...p, [category]: { ...p[category], saving: false } }));
    }
  };

  const handleAddDeadline = async () => {
    const { date, context } = addDeadlineState;
    if (!date.trim() || !context.trim()) return;
    setAddDeadlineState((p) => ({ ...p, saving: true }));
    try {
      const created = await v2Api.rfp.addDeadline(rfpId, { date: date.trim(), context: context.trim() });
      setRfp((prev) => ({ ...prev, key_deadlines: [...(prev.key_deadlines || []), created] }));
      setAddDeadlineState({ open: false, date: "", context: "", saving: false });
    } catch {
      toast.error("Could not add deadline");
      setAddDeadlineState((p) => ({ ...p, saving: false }));
    }
  };

  const handleDeleteDeadline = async (index) => {
    try {
      await v2Api.rfp.deleteDeadline(rfpId, index);
      setRfp((prev) => ({
        ...prev,
        key_deadlines: (prev.key_deadlines || []).filter((_, i) => i !== index),
      }));
      toast.success("Deadline removed");
    } catch {
      toast.error("Could not remove deadline");
    }
  };

  const handleDeleteSection = async (sectionIndex) => {
    try {
      await v2Api.rfp.deleteSection(rfpId, sectionIndex);
      setSections((prev) => prev.filter((s) => s.section_index !== sectionIndex));
      toast.success("Section removed");
    } catch {
      toast.error("Could not remove section");
    }
  };

  const handleDeleteRequirementItem = async (itemId) => {
    try {
      await v2Api.briefItems.deleteRequirementItem(itemId);
      setRequirementItems((prev) => prev.filter((r) => r.id !== itemId));
      toast.success("Requirement removed");
    } catch {
      toast.error("Could not remove requirement");
    }
  };

  const handleDeleteComplianceItem = async (itemId) => {
    try {
      await v2Api.briefItems.deleteComplianceItem(itemId);
      setComplianceItems((prev) => prev.filter((c) => c.id !== itemId));
      toast.success("Compliance item removed");
    } catch {
      toast.error("Could not remove compliance item");
    }
  };

  const handleSaveReqEdit = async (item) => {
    const newText = reqEditState[item.id]?.text ?? item.text;
    if (!newText.trim() || newText.trim() === item.text) {
      setReqEditState((p) => { const n = {...p}; delete n[item.id]; return n; });
      return;
    }
    setReqEditState((p) => ({ ...p, [item.id]: { ...p[item.id], saving: true } }));
    try {
      const updated = await v2Api.briefItems.patchRequirementItem(item.id, { text: newText.trim() });
      setRequirementItems((prev) => prev.map((r) => r.id === item.id ? { ...r, ...updated } : r));
      setReqEditState((p) => { const n = {...p}; delete n[item.id]; return n; });
    } catch { toast.error("Could not save"); setReqEditState((p) => ({ ...p, [item.id]: { ...p[item.id], saving: false } })); }
  };

  const handleSaveCompEdit = async (item) => {
    const newText = compEditState[item.id]?.text ?? item.text;
    if (!newText.trim() || newText.trim() === item.text) {
      setCompEditState((p) => { const n = {...p}; delete n[item.id]; return n; });
      return;
    }
    setCompEditState((p) => ({ ...p, [item.id]: { ...p[item.id], saving: true } }));
    try {
      const updated = await v2Api.briefItems.patchComplianceItem(item.id, { text: newText.trim() });
      setComplianceItems((prev) => prev.map((c) => c.id === item.id ? { ...c, ...updated } : c));
      setCompEditState((p) => { const n = {...p}; delete n[item.id]; return n; });
    } catch { toast.error("Could not save"); setCompEditState((p) => ({ ...p, [item.id]: { ...p[item.id], saving: false } })); }
  };

  const handlePatchDeadline = async (index, payload) => {
    try {
      const updated = await v2Api.rfp.patchDeadline(rfpId, index, payload);
      setRfp((prev) => {
        const dl = [...(prev.key_deadlines || [])];
        dl[index] = updated;
        return { ...prev, key_deadlines: dl };
      });
      setDeadlineEditState((p) => { const n = {...p}; delete n[index]; return n; });
    } catch { toast.error("Could not update deadline"); }
  };

  // Fetch page images for a section - inline loading
  const handleViewPageImages = async (section) => {
    // If already loaded, just toggle visibility
    if (sectionPageImages[section.id]) {
      setSectionPageImages(prev => ({...prev, [section.id]: null}));
      return;
    }
    
    setLoadingPageImages(prev => ({...prev, [section.id]: true}));
    
    try {
      // Try to fetch page images if PDF - pages are 1-indexed for pdf2image
      const startPage = (section.page_start || 0) + 1;  // Convert to 1-indexed
      const endPage = (section.page_end || 0) + 1;
      
      const pagesRes = await axios.get(`${API}/rfp/${rfpId}/pages-range`, {
        params: {
          start_page: startPage,
          end_page: endPage
        }
      });
      if (pagesRes.data.pages?.length > 0) {
        setSectionPageImages(prev => ({...prev, [section.id]: pagesRes.data.pages}));
      } else {
        toast.info("Page images not available for this section");
      }
    } catch (error) {
      console.log("Page images not available:", error);
      toast.error("Failed to load page images");
    } finally {
      setLoadingPageImages(prev => ({...prev, [section.id]: false}));
    }
  };

  // One-shot lazy fetcher: when the user first expands any "Original"
  // panel, fetch full bodies for ALL sections and merge them in. The
  // initial list call is body-less to keep payload small; without this
  // merge, "Original" only shows the 400-char body_preview, which
  // looks truncated mid-sentence.
  const fullBodiesLoadedRef = useRef(false);
  const [bodiesLoading, setBodiesLoading] = useState(false);

  // Reflow PDF-extracted body text so it reads as prose. PDFs encode
  // hard line breaks at the visual end of every printed line; left as-is
  // with `whitespace-pre-wrap`, prose appears chopped at every column
  // edge ("the contract ab\ninitio…"). We:
  //   - join lines hyphenated at column breaks ("deli-\nvery" → "delivery")
  //   - collapse single newlines inside a paragraph to a single space
  //   - preserve double-newlines as paragraph breaks
  //   - leave list-like lines (starting with "- " / "*" / "1." / "a)") alone
  //     so bulleted clauses don't reflow into one giant blob.
  /**
   * Clean and reflow OCR-extracted RFP body text for human reading.
   *
   * The raw PyPDF2 / OCR output that lands in section.body has three
   * recurring readability problems:
   *
   *  1. Soft-hyphenation across line breaks ("certifi-\ncate") —
   *     pre-existing fix.
   *  2. OCR-induced INTRA-word spaces: "lice nsed", "Encrypt ion",
   *     "B anning", "Organizati on", "Certif ying". These come from
   *     proportional-width PDFs where the OCR misjudges glyph gaps.
   *  3. Number + time fragments stuck together: "at/after 0 1:00 PM"
   *     should be "at/after 01:00 PM".
   *  4. Numbered tender clauses (1.0 / 2.0 / 3.0 …) run together in
   *     one wall paragraph instead of starting fresh lines.
   *
   * We patch (2)–(4) with conservative, dictionary-free heuristics. No
   * heuristic is perfect — the rules below favour false-negatives over
   * false-positives so they never merge words that shouldn't be merged.
   */
  const reflowBody = (text) => {
    if (!text) return "";
    let s = String(text);

    // (1) soft-hyphenation across newlines.
    s = s.replace(/([A-Za-z])-\n([a-z])/g, "$1$2");
    // Same pattern but space-separated (when newlines were already
    // collapsed elsewhere): "certifi- cate" → "certificate".
    s = s.replace(/([A-Za-z])-\s+([a-z])/g, "$1$2");

    // Normalise CRLF.
    s = s.replace(/\r\n/g, "\n");

    // (3) "at/after 0 1:00 PM" → "at/after 01:00 PM"
    s = s.replace(/\b0\s+(\d{1,2}:\d{2})/g, "0$1");

    // (2a) Common English suffix re-merge. Catches "lice nsed",
    // "Encrypt ion", "Organizati on", "Certif ying", "exten ded",
    // "prov ided", etc. The lookbehind ensures the trailing fragment
    // is short (1-5 chars) and the leading stem is 3+ chars so we don't
    // accidentally fuse two real words. Whitelist of suffixes so e.g.
    // "the air" never becomes "their".
    s = s.replace(
      /\b([A-Za-z]{3,})\s+(ion|ions|ing|ings|ed|er|ers|est|ly|ment|ments|tion|tions|able|ible|ity|ities|ous|ful|less|ance|ence|ant|ent|al|als|ic|ics|ish|ism|nse|nsed|nses|nced|ncing|cted|cating|cation|gned|gning|gement)\b/g,
      "$1$2",
    );

    // (2b) Standalone single capital fragment followed by a 3+ char
    // lowercase word: "B anning" → "Banning", "P roperty" → "Property".
    // Bounded by an explicit space-before so "I am" / "A few" are safe
    // (they're at sentence start, not mid-sentence).
    s = s.replace(
      /(?<=[a-z,;:.\-])\s+([A-Z])\s+([a-z]{3,})\b/g,
      " $1$2",
    );

    // (4) Insert paragraph break before each numbered tender clause
    // (e.g. "3.0 Bid should be …") when it appears mid-paragraph.
    s = s.replace(/([.!?:])\s+(\d{1,2}\.\d\s+[A-Z])/g, "$1\n\n$2");
    // Also break before "(a)" / "(b)" lettered sub-clauses if they
    // appear inline.
    s = s.replace(/([.!?:])\s+(\([a-zA-Z]\)\s+[A-Z])/g, "$1\n\n$2");

    // Collapse multi-spaces and stray spaces before punctuation.
    s = s.replace(/[ \t]{2,}/g, " ");
    s = s.replace(/\s+([,.;:!?])/g, "$1");

    // Split into paragraphs on blank lines, then reflow each.
    const paras = s.split(/\n{2,}/).map((p) => {
      const lines = p.split("\n");
      // If most lines look like list items, keep the layout.
      const listLike = lines.filter((l) =>
        /^\s*(?:[-*•]|\d+[.)]|[a-zA-Z][.)])\s+/.test(l)
      ).length;
      if (listLike >= Math.max(2, Math.ceil(lines.length * 0.4))) return p;
      // Else collapse mid-paragraph newlines to spaces.
      return lines.map((l) => l.trim()).filter(Boolean).join(" ");
    });
    return paras.join("\n\n");
  };
  const ensureFullBodies = useCallback(async () => {
    if (fullBodiesLoadedRef.current || bodiesLoading) return;
    setBodiesLoading(true);
    try {
      const data = await v2Api.rfp.sections(rfpId, { includeBody: true });
      const byIdx = new Map(
        (Array.isArray(data) ? data : []).map((s) => [s.section_index, s])
      );
      setSections((prev) => prev.map((s) => {
        const full = byIdx.get(s.section_index);
        if (!full) return s;
        return {
          ...s,
          content: full.body || s.content,
          body_truncated: !!full.body_truncated,
          body_full_length: full.body_full_length ?? s.body_full_length,
        };
      }));
      fullBodiesLoadedRef.current = true;
    } catch (err) {
      console.error("Failed to load full section bodies:", err);
    } finally {
      setBodiesLoading(false);
    }
  }, [rfpId, bodiesLoading]);

  const toggleSectionExpand = (sectionId) => {
    setExpandedSections(prev => {
      const next = { ...prev, [sectionId]: !prev[sectionId] };
      if (next[sectionId]) ensureFullBodies();
      return next;
    });
  };

  // Effective dept comes from the section's AI-classified department_id.
  const getEffectiveDept = (section) => section.department ?? null;

  const getFilteredSections = () => {
    if (activeDepartment === "unassigned") {
      return sections.filter((s) => !s.department_id);
    }
    if (activeDepartment === "all") return sections;
    return sections.filter((s) => getEffectiveDept(s) === activeDepartment);
  };

  const formatFileSize = (bytes) => {
    // null / undefined / 0 → em-dash, not "0 B". Legacy RFPs uploaded
    // before file_size_bytes was populated would otherwise display "0 B"
    // even on multi-MB PDFs.
    if (bytes == null || bytes === 0) return "—";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen" data-testid="rfp-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
      </div>
    );
  }

  if (!rfp) {
    return (
      <div className="p-8 text-center" data-testid="rfp-not-found">
        <p className="text-[#64748B]">RFP not found</p>
        <Button onClick={() => navigate("/")} className="mt-4">
          Go to Dashboard
        </Button>
      </div>
    );
  }

  const isProcessing = rfp.status === "processing";

  return (
    <div className="p-8" data-testid="rfp-analysis">
      {/* Processing Progress */}
      {isProcessing && (
        <Card className="border-[#E2E8F0] mb-6" data-testid="processing-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
              <div className="flex-1">
                <p className="font-semibold text-[#0F172A]">
                  Analyzing your RFP document...
                </p>
                <p className="text-sm text-[#64748B] mt-1">
                  This may take a few minutes for large documents
                </p>
                <Progress value={rfp.processing_progress || 0} className="h-2 mt-3" />
                <p className="text-xs text-[#64748B] mt-1">
                  {rfp.processing_progress || 0}% complete
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-[#F1F5F9] p-1">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="requirements" data-testid="tab-requirements">Requirements</TabsTrigger>
          <TabsTrigger value="sections" data-testid="tab-sections">Sections</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>
          <TabsTrigger value="tables" data-testid="tab-tables">Tables</TabsTrigger>
          <TabsTrigger value="diagrams" data-testid="tab-diagrams">Diagrams</TabsTrigger>
          <TabsTrigger value="deadlines" data-testid="tab-deadlines">Deadlines</TabsTrigger>
          <TabsTrigger value="boq" data-testid="tab-boq">BOQ</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Completeness checker banner — surfaces is_default=true
              templates that found ZERO matching sections during the
              classifier overlay. Sits at the very top so a PM can't
              start reviewing a partial parse without seeing what's
              missing. Hidden when nothing's missing or the workspace
              has no defaults configured. */}
          {Array.isArray(sectionsSummary?.missing_defaults)
            && sectionsSummary.missing_defaults.length > 0 && (
            <Card className="border-amber-300 bg-amber-50/40">
              <CardContent className="py-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                  <div className="text-sm text-amber-900 flex-1 min-w-0">
                    <p className="font-semibold">
                      {sectionsSummary.missing_defaults.length}
                      {" of "}
                      {sectionsSummary.default_template_count}
                      {" required section"}
                      {sectionsSummary.default_template_count === 1 ? "" : "s"}
                      {" not found in this tender."}
                    </p>
                    {/* Each missing template becomes a clickable chip
                        that opens a small popover with metadata + the
                        action shortcuts. */}
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      {sectionsSummary.missing_defaults.map((m) => (
                        <MissingTemplateChip key={m.code} meta={m} />
                      ))}
                    </div>
                    <p className="text-[11px] text-amber-800/80 mt-2 leading-relaxed">
                      "Required sections" are per-project compliance
                      checklist templates (
                      <Link to="/settings?tab=templates" className="underline">
                        Settings → Section templates
                      </Link>
                      ), separate from the per-project Section Taxonomy
                      override you set on the project page. Templates
                      declare "this RFP must surface a Payment Terms
                      section"; Taxonomy steers how each detected
                      section is summarised.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {/* Review Progress + Dept breakdown + Flags */}
          {(() => {
            const departments = rfpContext?.departments ?? [];
            const deptMap = {};
            const unassignedSections = [];
            const needsReviewSections = [];

            sections.forEach((sec) => {
              const idx = sec.section_index ?? sec.index;
              const isReviewed = !!sectionReviews[idx]?.compliance_signal;
              if (!sec.department_id) {
                unassignedSections.push(sec);
              } else {
                const dId = String(sec.department_id);
                if (!deptMap[dId]) {
                  const dept = departments.find((d) => String(d.id) === dId);
                  deptMap[dId] = { name: dept?.name || "Unknown dept", assigned: 0, reviewed: 0 };
                }
                deptMap[dId].assigned++;
                if (isReviewed) deptMap[dId].reviewed++;
                else needsReviewSections.push({ sec });
              }
            });

            const deptRows = Object.entries(deptMap).sort((a, b) => a[1].name.localeCompare(b[1].name));
            const flaggedReqs = requirementItems.filter((r) => r.review_status === "flagged");
            const flaggedComp = complianceItems.filter((c) => c.review_status === "flagged");
            const hasData = sections.length > 0 || requirementItems.length > 0;

            if (!hasData) {
              return (
                <Card className="border-[#E2E8F0]">
                  <CardContent className="py-12 text-center text-[#64748B]">
                    <BarChart3 className="w-10 h-10 mx-auto text-slate-200 mb-3" />
                    <p className="text-sm">Progress data will appear once sections are assigned and reviewed.</p>
                  </CardContent>
                </Card>
              );
            }

            return (
              <>
                {/* Summary strip */}
                {reviewProgress && (
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { label: "Sections reviewed", key: "sections", color: "bg-indigo-500", icon: <ListChecks className="w-4 h-4 text-indigo-500" /> },
                      { label: "Requirements done", key: "requirements", color: "bg-blue-500",   icon: <CheckCircle2 className="w-4 h-4 text-blue-500" /> },
                      { label: "Compliance done",   key: "compliance",   color: "bg-emerald-500", icon: <Shield className="w-4 h-4 text-emerald-500" /> },
                    ].map(({ label, key, color, icon }) => {
                      const p = reviewProgress[key];
                      const pct = p.total > 0 ? Math.round((p.reviewed / p.total) * 100) : 0;
                      return (
                        <Card key={key} className="border-[#E2E8F0]">
                          <CardContent className="py-4 px-5">
                            <div className="flex items-center gap-2 mb-2">{icon}<span className="text-xs text-slate-500">{label}</span></div>
                            <div className="text-2xl font-bold text-[#0F172A]">{p.reviewed}<span className="text-base font-normal text-slate-400">/{p.total}</span></div>
                            <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
                            </div>
                            <p className="text-[11px] text-slate-400 mt-1">{pct}% complete</p>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}

                {/* Dept-wise breakdown */}
                <Card className="border-[#E2E8F0]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-500" /> Department Progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-slate-100">
                      {deptRows.map(([dId, row]) => {
                        const pct = row.assigned > 0 ? Math.round((row.reviewed / row.assigned) * 100) : 0;
                        const allDone = row.reviewed === row.assigned && row.assigned > 0;
                        return (
                          <div key={dId} className="flex items-center gap-4 px-5 py-3">
                            <div className="w-36 shrink-0">
                              <p className="text-sm font-medium text-[#0F172A] truncate">{row.name}</p>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full transition-all ${allDone ? "bg-emerald-500" : "bg-indigo-400"}`}
                                    style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-xs text-slate-500 shrink-0">{row.reviewed}/{row.assigned} sections</span>
                              </div>
                            </div>
                            <span className={`shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full border ${
                              allDone
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : row.reviewed > 0
                                ? "bg-blue-50 text-blue-700 border-blue-200"
                                : "bg-slate-50 text-slate-500 border-slate-200"
                            }`}>
                              {allDone ? "Complete" : row.reviewed > 0 ? "In progress" : "Not started"}
                            </span>
                          </div>
                        );
                      })}
                      {unassignedSections.length > 0 && (
                        <div className="flex items-center gap-4 px-5 py-3 bg-slate-50/60">
                          <div className="w-36 shrink-0">
                            <p className="text-sm text-slate-400 italic">Unassigned</p>
                          </div>
                          <div className="flex-1">
                            <div className="h-1.5 bg-slate-100 rounded-full" />
                          </div>
                          <span className="shrink-0 text-[11px] text-slate-400">{unassignedSections.length} sections</span>
                        </div>
                      )}
                      {deptRows.length === 0 && unassignedSections.length === 0 && (
                        <p className="px-5 py-6 text-sm text-slate-400">No sections assigned yet.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Global flags */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Blockers */}
                  <Card className={`border-[#E2E8F0] ${flaggedReqs.length + flaggedComp.length > 0 ? "border-red-200" : ""}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2 text-red-700">
                        <AlertCircle className="w-4 h-4" /> Blockers
                        <span className="ml-auto text-xs font-normal text-slate-400">{flaggedReqs.length + flaggedComp.length}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {flaggedReqs.length + flaggedComp.length === 0 ? (
                        <p className="text-xs text-slate-400">No blockers flagged.</p>
                      ) : (
                        <ul className="space-y-2">
                          {flaggedReqs.map((r) => (
                            <li key={r.id} className="text-xs flex items-start gap-1.5">
                              <span className="shrink-0 mt-0.5 w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5" />
                              <span className="text-slate-700 line-clamp-2">{r.text}</span>
                              <button type="button" onClick={() => setActiveTab("requirements")}
                                className="shrink-0 text-blue-400 hover:text-blue-600 ml-auto">
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            </li>
                          ))}
                          {flaggedComp.map((c) => (
                            <li key={c.id} className="text-xs flex items-start gap-1.5">
                              <span className="shrink-0 mt-0.5 w-1.5 h-1.5 rounded-full bg-rose-400 mt-1.5" />
                              <span className="text-slate-700 line-clamp-2">{c.text}</span>
                              <button type="button" onClick={() => setActiveTab("compliance")}
                                className="shrink-0 text-blue-400 hover:text-blue-600 ml-auto">
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>

                  {/* Under review */}
                  <Card className={`border-[#E2E8F0] ${needsReviewSections.length > 0 ? "border-amber-200" : ""}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2 text-amber-700">
                        <Clock className="w-4 h-4" /> Under Review
                        <span className="ml-auto text-xs font-normal text-slate-400">{needsReviewSections.length}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {needsReviewSections.length === 0 ? (
                        <p className="text-xs text-slate-400">All assigned sections have been reviewed.</p>
                      ) : (
                        <ul className="space-y-2">
                          {needsReviewSections.slice(0, 8).map(({ sec }) => {
                            const dept = departments.find((d) => String(d.id) === String(sec.department_id));
                            return (
                              <li key={sec.section_index} className="text-xs flex items-start gap-1.5">
                                <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-slate-700 line-clamp-1">{sec.title || `Section ${sec.section_index + 1}`}</p>
                                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                    {dept && (
                                      <span className="text-[10px] text-slate-500 font-medium">{dept.name}</span>
                                    )}
                                    <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-700">
                                      Assigned
                                    </span>
                                  </div>
                                </div>
                                <button type="button"
                                  onClick={() => {
                                    setActiveTab("sections");
                                    setTimeout(() => {
                                      document.querySelector(`[data-testid="section-${sec.section_index}"]`)
                                        ?.scrollIntoView({ behavior: "smooth", block: "start" });
                                    }, 150);
                                  }}
                                  className="shrink-0 text-blue-400 hover:text-blue-600">
                                  <ExternalLink className="w-3 h-3" />
                                </button>
                              </li>
                            );
                          })}
                          {needsReviewSections.length > 8 && (
                            <p className="text-[11px] text-slate-400">+{needsReviewSections.length - 8} more</p>
                          )}
                        </ul>
                      )}
                    </CardContent>
                  </Card>

                  {/* Gaps — unassigned */}
                  <Card className={`border-[#E2E8F0] ${unassignedSections.length > 0 ? "border-slate-300" : ""}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-600">
                        <AlertTriangle className="w-4 h-4" /> Gaps
                        <span className="ml-auto text-xs font-normal text-slate-400">{unassignedSections.length}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {unassignedSections.length === 0 ? (
                        <p className="text-xs text-slate-400">All sections are assigned.</p>
                      ) : (
                        <ul className="space-y-2">
                          {unassignedSections.slice(0, 8).map((sec) => (
                            <li key={sec.section_index} className="text-xs flex items-start gap-1.5">
                              <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5" />
                              <span className="text-slate-700 line-clamp-2">{sec.title || `Section ${sec.section_index + 1}`}</span>
                              <button type="button"
                                onClick={() => {
                                  setActiveTab("sections");
                                  setTimeout(() => {
                                    document.querySelector(`[data-testid="section-${sec.section_index}"]`)
                                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                                  }, 150);
                                }}
                                className="shrink-0 text-blue-400 hover:text-blue-600 ml-auto">
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            </li>
                          ))}
                          {unassignedSections.length > 8 && (
                            <p className="text-[11px] text-slate-400">+{unassignedSections.length - 8} more</p>
                          )}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </>
            );
          })()}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Executive Summary */}
            <div className="lg:col-span-2">
              <Card className="border-[#E2E8F0]" data-testid="summary-card">
                <CardHeader>
                  <CardTitle className="font-['Manrope'] text-lg font-bold">
                    Executive Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="max-h-[500px] overflow-y-auto">
                  {rfp.overall_summary ? (
                    <div className="prose prose-sm max-w-none text-[#475569] leading-relaxed whitespace-pre-line">
                      <GlossaryHover
                        text={rfp.overall_summary
                          .replace(/\*\*/g, '')
                          .replace(/---/g, '')
                          .replace(/^\s+/gm, '')
                          .trim()}
                        glossary={sectionsSummary?.glossary}
                      />
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-[#64748B] text-sm mb-3">
                        {isProcessing ? "Summary will be available after processing…" : "Executive summary not yet generated."}
                      </p>
                      {!isProcessing && (
                        <button
                          type="button"
                          disabled={regeneratingExec}
                          onClick={async () => {
                            setRegeneratingExec(true);
                            try {
                              const r = await v2.post(
                                `/rfp/${rfpId}/executive-summary/regenerate`,
                                null,
                                { timeout: 120000 },
                              );
                              if (r?.data?.executive) {
                                const rfpData = await v2Api.rfp.get(rfpId);
                                setRfp(adaptV2Rfp(rfpData));
                                rfpContext?.refresh();
                                toast.success("Executive summary generated");
                              }
                            } catch (err) {
                              toast.error(
                                err?.response?.data?.detail
                                || err?.message
                                || "Could not generate executive summary",
                              );
                            } finally {
                              setRegeneratingExec(false);
                            }
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                        >
                          {regeneratingExec ? (
                            <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                          ) : (
                            <>Generate executive summary</>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Quick Stats */}
            <div className="space-y-4">
              <Card className="border-[#E2E8F0]" data-testid="stats-card">
                <CardHeader className="pb-3">
                  <CardTitle className="font-['Manrope'] text-lg font-bold">
                    Document Stats
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#64748B]">Total Sections</span>
                    <span className="font-semibold">{sectionsSummary?.total_sections || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#64748B]">Pages</span>
                    <span className="font-semibold">{rfp.total_pages || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#64748B]">Deadlines Found</span>
                    <span className="font-semibold">{rfp.key_deadlines?.length || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#64748B]">Compliance Items</span>
                    <span className="font-semibold">{rfp.compliance_status?.total_items || 0}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Reconciled headline numbers — EMD / PBG / validity
                  with the canonical value + a "where it appears"
                  footer + a conflict warning when sections disagree. */}
              <ReconciledNumbersCard reconciled={sectionsSummary?.reconciled} />

            </div>
          </div>
        </TabsContent>

        {/* Requirements Tab — structured brief extracted by Gemini in
            rfp_pipeline.requirements_brief_s. Rides on
            rfp.requirements_brief (populated by adaptV2Rfp from
            analysis_summary.requirements_brief). Empty dict on legacy
            analyses → shows a friendly "Re-analyse to populate" hint. */}
        <TabsContent value="requirements" className="space-y-6">
          {(() => {
            const brief = rfp?.requirements_brief || {};
            const hasAny = Object.keys(brief).length > 0 && (
              brief.scope_summary ||
              (brief.quantities || []).length ||
              (brief.timeline || []).length ||
              (brief.deadlines || []).length ||
              (brief.manpower || []).length ||
              (brief.maintenance && (brief.maintenance.tenure_months ||
                (brief.maintenance.scope || []).length ||
                (brief.maintenance.sla || []).length)) ||
              (brief.certifications_required || []).length ||
              (brief.compliance_items || []).length ||
              (brief.technical_requirements || []).length ||
              (brief.services_requirements || []).length ||
              (brief.legal_requirements || []).length ||
              (brief.financial_requirements && Object.keys(brief.financial_requirements).length)
            );
            if (!hasAny) {
              return (
                <Card className="border-[#E2E8F0]">
                  <CardContent className="py-10 text-center text-[#64748B]">
                    <p className="text-base mb-2">Requirements brief not yet extracted for this RFP.</p>
                    <p className="text-sm mb-4">
                      The brief pulls quantities, timeline, manpower, maintenance,
                      certifications, compliance, technical, services, financial
                      and legal requirements into one place.
                    </p>
                    <p className="text-xs text-slate-400 mb-3">
                      This is the heaviest single LLM call in the pipeline and
                      occasionally fails on big tenders (timeout / malformed
                      JSON). Click below to regenerate it without re-running the
                      whole analysis.
                    </p>
                    <button
                      type="button"
                      disabled={regeneratingBrief}
                      onClick={async () => {
                        setRegeneratingBrief(true);
                        try {
                          const r = await v2.post(
                            `/rfp/${rfpId}/requirements-brief/regenerate`,
                            null,
                            { timeout: 180000 },
                          );
                          if (r?.data?.requirements_brief) {
                            // Refresh both the sections summary (requirements tab)
                            // and the full rfp (executive summary in Brief tab).
                            const [summary, rfpData] = await Promise.all([
                              v2Api.rfp.sectionsSummary(rfpId),
                              v2Api.rfp.get(rfpId),
                            ]);
                            setSectionsSummary(summary);
                            setRfp(adaptV2Rfp(rfpData));
                            rfpContext?.refresh();
                            toast.success("Requirements brief generated");
                          }
                        } catch (err) {
                          toast.error(
                            err?.response?.data?.detail
                            || err?.message
                            || "Could not regenerate brief",
                          );
                        } finally {
                          setRegeneratingBrief(false);
                        }
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                    >
                      {regeneratingBrief ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                      ) : (
                        <>Generate requirements brief</>
                      )}
                    </button>
                  </CardContent>
                </Card>
              );
            }
            // When DB items are loaded, show them with review controls;
            // otherwise fall back to the raw JSON display.
            if (requirementItems.length > 0) {
              const CATEGORY_LABELS = {
                quantity: "Quantities", timeline: "Timeline & Milestones",
                manpower: "Manpower", maintenance: "Maintenance / O&M",
                certifications: "Certifications Required", technical: "Technical Requirements",
                services: "Services / Scope of Work", financial: "Financial",
              };
              const STATUS_CFG = {
                pending:  { cls: "border-slate-200 text-slate-400",                     icon: <Clock className="w-3 h-3" />,       label: "Pending" },
                reviewed: { cls: "bg-emerald-50 border-emerald-300 text-emerald-700",   icon: <CheckCircle2 className="w-3 h-3" />, label: "Reviewed" },
                flagged:  { cls: "bg-amber-50 border-amber-300 text-amber-700",         icon: <Flag className="w-3 h-3" />,        label: "Flagged" },
                na:       { cls: "bg-slate-50 border-slate-200 text-slate-400",         icon: <Minus className="w-3 h-3" />,       label: "N/A" },
              };
              const byCategory = requirementItems.reduce((acc, it) => {
                (acc[it.category] = acc[it.category] || []).push(it);
                return acc;
              }, {});
              return (
                <div className="space-y-4">
                  {brief.scope_summary && (
                    <Card className="border-[#E2E8F0] bg-blue-50/30">
                      <CardHeader><CardTitle className="text-base">Scope at a Glance</CardTitle></CardHeader>
                      <CardContent><p className="text-sm text-[#0F172A] whitespace-pre-line">{brief.scope_summary}</p></CardContent>
                    </Card>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(byCategory).map(([cat, items]) => {
                      const addState = addItemState[cat] || { open: false, text: "" };
                      const done = items.filter(i => i.review_status !== "pending").length;
                      return (
                        <Card key={cat} className="border-[#E2E8F0]">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center justify-between">
                              <span>{CATEGORY_LABELS[cat] || cat} <span className="text-xs text-[#64748B] font-normal">({items.length})</span></span>
                              <span className={`text-xs font-normal ${done === items.length ? "text-emerald-600" : "text-[#64748B]"}`}>
                                {done}/{items.length} done
                              </span>
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <ul className="space-y-3">
                              {items.map((it) => {
                                const cfg = STATUS_CFG[it.review_status] || STATUS_CFG.pending;
                                const busy = reqItemsBusy[it.id];
                                const noteOpen = reqNoteState[it.id] !== undefined;
                                const noteVal = reqNoteState[it.id]?.note ?? it.review_note ?? "";
                                const isNa = it.review_status === "na";
                                const editOpen = reqEditState[it.id] !== undefined;
                                const editText = reqEditState[it.id]?.text ?? it.text;
                                const editSaving = reqEditState[it.id]?.saving;
                                return (
                                  <li key={it.id} className={`space-y-1.5 ${isNa ? "opacity-50" : ""}`}>
                                    <div className="flex items-start gap-2">
                                      {/* status chip */}
                                      <span className={`shrink-0 mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium ${cfg.cls}`}>
                                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : cfg.icon}
                                        {cfg.label}
                                      </span>
                                      <span className={`text-sm flex-1 ${isNa ? "line-through text-slate-400" : "text-[#0F172A]"}`}>{it.text}</span>
                                    </div>

                                    {/* inline edit form */}
                                    {editOpen && (
                                      <div className="pl-0.5 space-y-1.5">
                                        <Textarea
                                          value={editText}
                                          onChange={(e) => setReqEditState((p) => ({ ...p, [it.id]: { ...p[it.id], text: e.target.value } }))}
                                          rows={2} maxLength={600} className="text-xs" autoFocus
                                        />
                                        <div className="flex gap-1.5">
                                          <Button size="sm" className="h-6 text-xs" disabled={editSaving || !editText.trim()}
                                            onClick={() => handleSaveReqEdit(it)}>
                                            {editSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Save
                                          </Button>
                                          <Button size="sm" variant="ghost" className="h-6 text-xs" disabled={editSaving}
                                            onClick={() => setReqEditState((p) => { const n = {...p}; delete n[it.id]; return n; })}>
                                            Cancel
                                          </Button>
                                        </div>
                                      </div>
                                    )}

                                    {/* action row */}
                                    {!editOpen && (
                                      <div className="flex items-center gap-1.5 pl-0.5 flex-wrap">
                                        {/* Reviewed */}
                                        <button type="button" disabled={busy}
                                          onClick={() => it.review_status === "reviewed"
                                            ? handleSetReqStatus(it, "pending", "")
                                            : handleSetReqStatus(it, "reviewed")}
                                          className={`text-[11px] px-1.5 py-0.5 rounded border flex items-center gap-1 transition-colors disabled:opacity-50
                                            ${it.review_status === "reviewed"
                                              ? STATUS_CFG.reviewed.cls + " font-medium"
                                              : "border-slate-200 text-slate-400 hover:border-slate-400 hover:text-slate-600"}`}>
                                          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                                          Reviewed
                                        </button>

                                        {/* Flagged */}
                                        <button type="button" disabled={busy}
                                          onClick={() => it.review_status === "flagged"
                                            ? handleSetReqStatus(it, "pending", "")
                                            : setReqNoteState((p) => ({ ...p, [it.id]: { note: it.review_note || "", pendingStatus: "flagged" } }))}
                                          className={`text-[11px] px-1.5 py-0.5 rounded border flex items-center gap-1 transition-colors disabled:opacity-50
                                            ${it.review_status === "flagged"
                                              ? STATUS_CFG.flagged.cls + " font-medium"
                                              : "border-slate-200 text-slate-400 hover:border-slate-400 hover:text-slate-600"}`}>
                                          <Flag className="w-3 h-3" /> Flag
                                        </button>

                                        {/* N/A */}
                                        <button type="button" disabled={busy}
                                          onClick={() => handleSetReqStatus(it, isNa ? "pending" : "na", "")}
                                          className={`text-[11px] px-1.5 py-0.5 rounded border flex items-center gap-1 transition-colors disabled:opacity-50
                                            ${isNa
                                              ? STATUS_CFG.na.cls + " font-medium"
                                              : "border-slate-200 text-slate-400 hover:border-slate-400 hover:text-slate-600"}`}>
                                          <Minus className="w-3 h-3" /> N/A
                                        </button>

                                        {/* Add note */}
                                        {it.review_status === "reviewed" && !noteOpen && (
                                          <button type="button"
                                            onClick={() => setReqNoteState((p) => ({ ...p, [it.id]: { note: it.review_note || "", pendingStatus: "reviewed" } }))}
                                            className="text-[11px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5">
                                            <MessageSquare className="w-3 h-3" />
                                            {it.review_note ? "Edit note" : "Add note"}
                                          </button>
                                        )}

                                        {/* Edit text (admin) */}
                                        {userIsAdmin && (
                                          <button type="button"
                                            onClick={() => setReqEditState((p) => ({ ...p, [it.id]: { text: it.text } }))}
                                            className="text-[11px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5">
                                            <Pencil className="w-3 h-3" /> Edit
                                          </button>
                                        )}

                                        {/* source section link */}
                                        {it.section_index != null && (
                                          <button type="button"
                                            onClick={() => {
                                              setActiveTab("sections");
                                              setTimeout(() => {
                                                const el = document.querySelector(`[data-testid="section-${String(it.section_index)}"]`);
                                                el?.scrollIntoView({ behavior: "smooth", block: "start" });
                                              }, 150);
                                            }}
                                            className="text-[11px] text-blue-500 hover:text-blue-700 flex items-center gap-0.5 ml-auto">
                                            <ExternalLink className="w-3 h-3" />
                                            Source §{it.section_index + 1}
                                          </button>
                                        )}
                                      </div>
                                    )}

                                    {/* inline note form */}
                                    {noteOpen && (
                                      <div className="pl-0.5 space-y-1.5">
                                        <Textarea
                                          value={noteVal}
                                          onChange={(e) => setReqNoteState((p) => ({ ...p, [it.id]: { ...p[it.id], note: e.target.value } }))}
                                          placeholder={reqNoteState[it.id]?.pendingStatus === "flagged" ? "Why flagged? (required)" : "Note (optional)"}
                                          rows={2} maxLength={600}
                                          className="text-xs"
                                          autoFocus
                                        />
                                        <div className="flex gap-1.5">
                                          <Button size="sm" className="h-6 text-xs"
                                            disabled={busy || (reqNoteState[it.id]?.pendingStatus === "flagged" && !noteVal.trim())}
                                            onClick={() => handleSetReqStatus(it, reqNoteState[it.id]?.pendingStatus || it.review_status, noteVal)}>
                                            {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Save
                                          </Button>
                                          <Button size="sm" variant="ghost" className="h-6 text-xs" disabled={busy}
                                            onClick={() => setReqNoteState((p) => { const n = {...p}; delete n[it.id]; return n; })}>
                                            Cancel
                                          </Button>
                                        </div>
                                      </div>
                                    )}

                                    {/* existing note display */}
                                    {!noteOpen && it.review_note && (
                                      <p className="pl-0.5 text-xs text-slate-500 bg-slate-50 rounded px-2 py-1 italic">
                                        {it.review_note}
                                      </p>
                                    )}

                                    {/* reviewer attribution */}
                                    {it.reviewer_name && (
                                      <p className="pl-0.5 text-[10px] text-slate-400">
                                        by {it.reviewer_name}
                                        {it.reviewed_at && <> · {new Date(it.reviewed_at).toLocaleDateString()}</>}
                                      </p>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>

                            {/* add manual item */}
                            <div className="mt-3 pt-3 border-t border-[#E2E8F0]">
                              {!addState.open ? (
                                <button type="button"
                                  onClick={() => setAddItemState((p) => ({ ...p, [cat]: { open: true, text: "" } }))}
                                  className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1">
                                  <Plus className="w-3 h-3" /> Add requirement
                                </button>
                              ) : (
                                <div className="space-y-1.5">
                                  <Textarea
                                    value={addState.text}
                                    onChange={(e) => setAddItemState((p) => ({ ...p, [cat]: { ...p[cat], text: e.target.value } }))}
                                    placeholder="Describe the requirement…"
                                    rows={2} maxLength={600}
                                    className="text-xs"
                                  />
                                  <div className="flex gap-1.5">
                                    <Button size="sm" className="h-6 text-xs" disabled={!addState.text.trim() || addState.saving}
                                      onClick={() => handleAddReqItem(cat, addState.text)}>
                                      {addState.saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Add
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-6 text-xs"
                                      onClick={() => setAddItemState((p) => ({ ...p, [cat]: { open: false, text: "" } }))}>
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            }

            const ListCard = ({ title, items, render }) => {
              const rows = Array.isArray(items) ? items.filter(Boolean) : [];
              if (!rows.length) return null;
              return (
                <Card className="border-[#E2E8F0]">
                  <CardHeader><CardTitle className="text-base">{title} <span className="text-xs text-[#64748B]">({rows.length})</span></CardTitle></CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm text-[#0F172A]">
                      {rows.map((it, i) => <li key={i} className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#007AFF] flex-shrink-0" /><span>{render ? render(it) : it}</span></li>)}
                    </ul>
                  </CardContent>
                </Card>
              );
            };
            const fin = brief.financial_requirements || {};
            const maint = brief.maintenance || {};
            return (
              <div className="space-y-4">
                {brief.scope_summary && (
                  <Card className="border-[#E2E8F0] bg-blue-50/30">
                    <CardHeader><CardTitle className="text-base">Scope at a Glance</CardTitle></CardHeader>
                    <CardContent><p className="text-sm text-[#0F172A] whitespace-pre-line">{brief.scope_summary}</p></CardContent>
                  </Card>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ListCard title="Quantities" items={brief.quantities} render={(q) =>
                    <span>{q.item}{q.qty != null && <span className="text-[#64748B]"> — {q.qty}{q.uom ? ` ${q.uom}` : ""}</span>}</span>
                  } />
                  <ListCard title="Timeline & Milestones" items={brief.timeline} render={(t) =>
                    <span>{t.milestone}{t.date && <span className="text-[#64748B]"> — {t.date}</span>}</span>
                  } />
                  <ListCard title="Deadlines" items={brief.deadlines} render={(d) =>
                    <span>{d.event}{d.date && <span className="text-[#64748B]"> — {d.date}</span>}</span>
                  } />
                  <ListCard title="Manpower" items={brief.manpower} render={(m) =>
                    <span>{m.role}{m.count != null && <span className="text-[#64748B]"> × {m.count}</span>}{m.qualification && <div className="text-xs text-[#64748B]">{m.qualification}</div>}</span>
                  } />
                  {(maint.tenure_months || (maint.scope || []).length || (maint.sla || []).length) && (
                    <Card className="border-[#E2E8F0]">
                      <CardHeader><CardTitle className="text-base">Maintenance / O&amp;M</CardTitle></CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        {maint.tenure_months && <div><span className="text-[#64748B]">Tenure:</span> {maint.tenure_months} months</div>}
                        {(maint.scope || []).length > 0 && <div><div className="text-[#64748B] mb-1">Scope</div><ul className="space-y-1 ml-4 list-disc">{maint.scope.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
                        {(maint.sla || []).length > 0 && <div><div className="text-[#64748B] mb-1">SLA</div><ul className="space-y-1 ml-4 list-disc">{maint.sla.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
                      </CardContent>
                    </Card>
                  )}
                  <ListCard title="Certifications Required" items={brief.certifications_required} />
                  <ListCard title="Compliance" items={brief.compliance_items} />
                  <ListCard title="Technical Requirements" items={brief.technical_requirements} />
                  <ListCard title="Services / Scope of Work" items={brief.services_requirements} />
                  <ListCard title="Legal / Contract Clauses" items={brief.legal_requirements} />
                  {(fin.emd || fin.pbg || fin.turnover_threshold_inr || fin.estimated_value_inr || fin.payment_terms || fin.bid_validity_days) && (
                    <Card className="border-[#E2E8F0]">
                      <CardHeader><CardTitle className="text-base">Financial</CardTitle></CardHeader>
                      <CardContent className="text-sm space-y-1">
                        {fin.emd && <div><span className="text-[#64748B]">EMD:</span> {fin.emd}</div>}
                        {fin.pbg && <div><span className="text-[#64748B]">PBG:</span> {fin.pbg}</div>}
                        {fin.turnover_threshold_inr && <div><span className="text-[#64748B]">Turnover threshold:</span> ₹{Number(fin.turnover_threshold_inr).toLocaleString("en-IN")}</div>}
                        {fin.estimated_value_inr && <div><span className="text-[#64748B]">Estimated value:</span> ₹{Number(fin.estimated_value_inr).toLocaleString("en-IN")}</div>}
                        {fin.payment_terms && <div><span className="text-[#64748B]">Payment terms:</span> {fin.payment_terms}</div>}
                        {fin.bid_validity_days && <div><span className="text-[#64748B]">Bid validity:</span> {fin.bid_validity_days} days</div>}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            );
          })()}
          <TaggedSectionsPanel
            tagType="requirement"
            allLinks={allSectionLinks}
            sections={sections}
            onGoToSection={(s) => { setActiveTab("sections"); setActiveDepartment("all"); }}
          />
        </TabsContent>

        {/* Sections Tab */}
        <TabsContent value="sections">
          <div className="space-y-6">
          {/* Priority #4 — user-authored "temp" sections live above the
              auto-detected list. They survive a re-analyse, unlike
              anything inside rfp.analysis['sections']. */}
          <ManualSectionsPanel rfpId={rfpId} departments={rfpContext?.departments ?? []} />

          {/* Department tabs */}
          {(() => {
            // Build tabs from section department_id classification.
            const deptNames = [...new Set(sections.map(getEffectiveDept).filter(Boolean))].sort();
            const tabs = [
              { key: "all", label: "All sections", count: sections.length, reviewed: null },
              ...deptNames.map((name) => {
                const deptSections = sections.filter((s) => getEffectiveDept(s) === name);
                const reviewed = deptSections.filter((s) => {
                  const idx = s.section_index ?? s.index;
                  return !!sectionReviews[idx]?.compliance_signal;
                }).length;
                return { key: name, label: name, count: deptSections.length, reviewed };
              }),
              {
                key: "unassigned",
                label: "Unassigned",
                count: sections.filter((s) => !s.department_id).length,
                reviewed: null,
              },
            ];
            return (
              <div className="border-b border-slate-200 -mx-1">
                <div className="flex gap-0 overflow-x-auto">
                  {tabs.map((t) => {
                    const active = activeDepartment === t.key;
                    const remaining = t.reviewed !== null ? t.count - t.reviewed : null;
                    return (
                      <button
                        key={t.key}
                        onClick={() => setActiveDepartment(t.key)}
                        className={`shrink-0 px-4 py-2.5 text-sm border-b-2 transition-colors whitespace-nowrap flex flex-col items-start gap-0.5 ${
                          active
                            ? "border-blue-600 text-blue-700 bg-blue-50/50"
                            : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                        }`}
                        data-testid={`filter-${t.key}`}
                      >
                        <span className="font-medium">{t.label}</span>
                        <span className={`text-[11px] ${active ? "text-blue-500" : "text-slate-400"}`}>
                          {t.count} section{t.count !== 1 ? "s" : ""}
                          {t.reviewed !== null && (
                            <span className="ml-1">
                              · <span className="text-emerald-600">{t.reviewed} done</span>
                              {remaining > 0 && <span className="text-amber-600"> · {remaining} left</span>}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Sections action bar */}
          <div className="flex items-center justify-between gap-3 mt-3">
            <div className="flex items-center gap-2">
              {/* Template matching — re-runs keyword classifier, no Gemini tokens */}
              <Button
                size="sm"
                variant="outline"
                disabled={reclassifying}
                className="gap-1.5 text-slate-600"
                title="Re-run keyword classifier against existing sections. Use after editing Section Taxonomy — no Gemini tokens spent."
                onClick={async () => {
                  setReclassifying(true);
                  try {
                    const r = await v2Api.rfp.reclassify(rfpId);
                    toast.success(`Template matching: ${r.newly_classified} new, ${r.changed_subsection} changed, ${r.unchanged} unchanged.`);
                    // Refresh sections so new department_id stamps are visible
                    const sectionsData = await v2Api.rfp.sections(rfpId);
                    setSections((Array.isArray(sectionsData) ? sectionsData : []).map(adaptV2Section));
                  } catch (err) {
                    toast.error(err?.response?.data?.detail ?? err?.message ?? "Template matching failed");
                  } finally {
                    setReclassifying(false);
                  }
                }}
              >
                {reclassifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListChecks className="w-3.5 h-3.5" />}
                {reclassifying ? "Matching…" : "Template matching"}
              </Button>
            </div>

            {/* Unassigned count label */}
            {activeDepartment === "unassigned" && (() => {
              const unassignedCount = sections.filter((s) => !s.department_id).length;
              if (!unassignedCount) return null;
              return (
                <span className="text-xs text-amber-700 font-medium">
                  {unassignedCount} section{unassignedCount !== 1 ? "s" : ""} unassigned
                </span>
              );
            })()}
          </div>

          {/* Sections List */}
          <div className="space-y-4">
            {getFilteredSections().length === 0 ? (
              <Card className="border-[#E2E8F0]">
                <CardContent className="p-8 text-center">
                  <FileText className="w-12 h-12 mx-auto text-[#E2E8F0] mb-3" />
                  <p className="text-[#64748B]">
                    {isProcessing ? "Sections will appear after processing..." : "No sections found"}
                  </p>
                </CardContent>
              </Card>
            ) : (
              getFilteredSections().map((section) => {
                const SectionIcon = sectionTypeIcons[section.section_type] || FileText;
                const sectionIdx = section.section_index ?? section.index;
                const isAssigned = !!section.department_id;
                const isReviewed = !!sectionReviews[sectionIdx]?.compliance_signal;
                return (
                  <Card
                    key={section.id}
                    className={`border-[#E2E8F0] ${isAssigned ? "border-l-2 border-l-indigo-300" : ""}`}
                    data-testid={`section-${section.id}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#F1F5F9] rounded-lg flex items-center justify-center">
                            <SectionIcon className="w-5 h-5 text-[#007AFF]" />
                          </div>
                          <div>
                            <CardTitle className="font-['Manrope'] text-base font-bold">
                              {section.title}
                            </CardTitle>
                            {(() => {
                              const effectiveDept = getEffectiveDept(section);
                              const DEPT_CLS = (name) =>
                                name === "Technical"    ? "bg-indigo-100 text-indigo-700" :
                                name === "Finance"      ? "bg-emerald-100 text-emerald-700" :
                                name === "Legal"        ? "bg-red-100 text-red-700" :
                                name === "Commercial"   ? "bg-blue-100 text-blue-700" :
                                name === "Procurement"  ? "bg-orange-100 text-orange-700" :
                                name === "Sales"        ? "bg-purple-100 text-purple-700" :
                                name === "Operations"   ? "bg-teal-100 text-teal-700" :
                                "bg-amber-100 text-amber-700";
                              return (
                                <div className="flex gap-1.5 mt-1 flex-wrap items-center">
                                  {effectiveDept && (
                                    <Badge className={`text-xs ${DEPT_CLS(effectiveDept)}`}>
                                      {effectiveDept}
                                    </Badge>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleSectionExpand(section.id)}
                            data-testid={`view-original-${section.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Original
                            {expandedSections[section.id] ? (
                              <ChevronUp className="w-4 h-4 ml-1" />
                            ) : (
                              <ChevronDown className="w-4 h-4 ml-1" />
                            )}
                          </Button>
                          <span className="text-xs text-[#64748B]">
                            Pages {section.page_start}-{section.page_end}
                          </span>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {section.summary ? (
                        <div className="mb-4">
                          <p className="text-sm text-[#64748B] font-medium mb-2">AI Summary</p>
                          <p className="text-sm text-[#0F172A] leading-relaxed whitespace-pre-line">
                            <GlossaryHover
                              text={section.summary
                                .replace(/#{1,6}\s*/g, '')
                                .replace(/\*\*/g, '')
                                .replace(/\*/g, '')
                                .replace(/---/g, '')
                                .split('\n')
                                .filter((ln) => {
                                  const t = ln.trim();
                                  if (!t) return true;
                                  const bulletPayload = t.match(/^[-•]\s*[A-Za-z][A-Za-z ]*:\s*(.*)$/);
                                  if (!bulletPayload) return true;
                                  const val = bulletPayload[1].trim().toLowerCase();
                                  if (!val) return false;
                                  if (/^(n\/a|none|none specified|not applicable|not specified|nil)\.?$/.test(val)) return false;
                                  return true;
                                })
                                .join('\n')
                                .replace(/^\s*[-•]\s*/gm, '• ')
                                .trim()}
                              glossary={sectionsSummary?.glossary}
                            />
                          </p>
                        </div>
                      ) : (
                        section.content && (
                          <div className="mb-4">
                            <p className="text-sm text-[#64748B] font-medium mb-2">Preview</p>
                            <p className="text-sm text-[#0F172A] leading-relaxed line-clamp-4">
                              {reflowBody(section.content).slice(0, 360)}
                              {reflowBody(section.content).length > 360 && "…"}
                            </p>
                          </div>
                        )
                      )}
                      {section.key_points?.length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm text-[#64748B] font-medium mb-2">Key Points</p>
                          <ul className="space-y-2">
                            {section.key_points.slice(0, 5).map((point, idx) => (
                              <li key={idx} className="flex gap-2 text-sm">
                                <CheckCircle2 className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                                <span>
                                  {point
                                    .replace(/#{1,6}\s*/g, '')
                                    .replace(/\*\*/g, '')
                                    .replace(/\*/g, '')
                                    .trim()}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {section.deadlines?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {section.deadlines.map((deadline, idx) => (
                            <Badge 
                              key={idx} 
                              variant="outline"
                              className="bg-red-50 border-red-200 text-red-700"
                            >
                              <Calendar className="w-3 h-3 mr-1" />
                              {deadline.date}
                            </Badge>
                          ))}
                        </div>
                      )}
                      
                      {/* Product Tagging — inline footer instead of a bulky
                          empty card. ProductTagger renders its own
                          "+ Tag Product" trigger when no products are tagged. */}
                      <div className="mb-2 flex items-center gap-2 text-xs text-[#64748B]">
                        <span className="font-medium">Linked products:</span>
                        <ProductTagger
                          rfpId={rfpId}
                          sectionIndex={section.section_index}
                          initialTags={section.product_tags || []}
                          sectionTitle={section.title}
                        />
                      </div>

                      {/* Section Links — BOQ / requirement / compliance / note chips */}
                      {section.section_index != null && (
                        <SectionLinksRow
                          rfpId={rfpId}
                          sectionIndex={section.section_index}
                          allLinks={allSectionLinks}
                          setAllLinks={setAllSectionLinks}
                        />
                      )}

                      {/* Original Content Section */}
                      {expandedSections[section.id] && (
                        <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                          <div className="flex items-center gap-2 mb-3">
                            <FileText className="w-4 h-4 text-[#64748B]" />
                            <p className="text-sm text-[#64748B] font-medium">Original RFP Content</p>
                          </div>
                          <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4 max-h-[70vh] overflow-y-auto">
                            {bodiesLoading && !fullBodiesLoadedRef.current ? (
                              <div className="flex items-center gap-2 text-sm text-[#64748B]">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Loading full section text…
                              </div>
                            ) : (
                              <div className="text-sm text-[#0F172A] leading-relaxed font-sans whitespace-pre-wrap break-words">
                                {reflowBody(section.content) || "Original content not available"}
                              </div>
                            )}
                          </div>
                          {section.body_truncated && (
                            <div className="mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 inline-flex items-center gap-1.5">
                              <AlertCircle className="w-3.5 h-3.5" />
                              Section body truncated to 200,000 chars
                              {section.body_full_length
                                ? ` (full length: ${section.body_full_length.toLocaleString()})`
                                : ""}
                              . Open the original PDF for the tail content.
                            </div>
                          )}
                          {isPdfRfp && (section.page_start >= 0 || section.page_end > 0) && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-3"
                              onClick={() => handleViewPageImages(section)}
                              disabled={loadingPageImages[section.id]}
                            >
                              {loadingPageImages[section.id] ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Image className="w-4 h-4 mr-2" />
                              )}
                              {sectionPageImages[section.id] ? "Hide Page Images" : `View Page Images (Pages ${section.page_start + 1}-${section.page_end + 1})`}
                            </Button>
                          )}
                          
                          {/* Inline Page Images Display */}
                          {sectionPageImages[section.id] && (
                            <div className="mt-4 space-y-4">
                              <div className="flex items-center gap-2 text-sm font-medium text-[#64748B]">
                                <Image className="w-4 h-4" />
                                Original RFP Pages (Pages {section.page_start + 1}-{section.page_end + 1})
                              </div>
                              {sectionPageImages[section.id].map((page, idx) => (
                                <div key={idx} className="border border-[#E2E8F0] rounded-lg overflow-hidden">
                                  <div className="bg-[#F1F5F9] px-4 py-2 text-sm font-medium text-[#64748B]">
                                    Page {page.page_number}
                                  </div>
                                  <img
                                    src={`data:image/png;base64,${page.image_base64}`}
                                    alt={`Page ${page.page_number}`}
                                    className="w-full"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* SectionComments removed for v2 — the legacy
                          component targets v1 /api/rfp/{id}/comments
                          endpoints that don't exist in v2, leaving a
                          spinner stuck forever between every card. The
                          v2 comments router uses {entity_type, entity_id}
                          shape and needs a separate FE port; tracked as
                          follow-up after the demo. */}

                      {/* Priority #1: per-section accept / reject / re-analyse.
                          ``section_index`` comes straight off the v2
                          payload via adaptV2Section. */}
                      <SectionReviewBar
                        rfpId={rfpId}
                        sectionIndex={section.section_index ?? null}
                        departmentId={section.department_id ?? null}
                      />

                      {/* Priority #3 + #5: per-section action row.
                          "View original" opens the cached
                          /page/{p}/image endpoint in a slide-over;
                          "Find exemplars" queries the Library against
                          this section's text so the user can pull
                          prior-art language for the clause they're
                          drafting right now. */}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {isPdfRfp
                          && (section.page_start >= 0 || section.page_end > 0) && (
                          <Button
                            variant="outline" size="sm"
                            onClick={() => setPdfDialogSection(section)}
                            className="h-7 text-xs gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                          >
                            <FileText className="w-3 h-3" />
                            View on PDF
                          </Button>
                        )}
                        <Button
                          variant="outline" size="sm"
                          onClick={() => setExemplarsSection(section)}
                          className="h-7 text-xs gap-1"
                          title="Find exemplars in the Library that match this section"
                        >
                          <BookOpen className="w-3 h-3" />
                          Find exemplars
                        </Button>
                        {section.section_index != null && (
                          <Button
                            variant="outline" size="sm"
                            onClick={() => setCrossValSection(section)}
                            className="h-7 text-xs gap-1"
                            title="Cross-validate this section against a product"
                          >
                            <Shield className="w-3 h-3" />
                            Cross-validate
                          </Button>
                        )}
                        {/* Priority #8 — inline plain-English summary.
                            Self-manages its open/close + cache state. */}
                        <SectionExplainer
                          rfpId={rfpId}
                          sectionIndex={section.section_index ?? null}
                        />
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
          </div>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="space-y-6">
          {(() => {
            const COMP_CFG = {
              pending:   { cls: "border-slate-200 text-slate-400",                   icon: <Clock className="w-3 h-3" />,       label: "Pending" },
              compliant: { cls: "bg-emerald-50 border-emerald-300 text-emerald-700", icon: <CheckCircle2 className="w-3 h-3" />, label: "Compliant" },
              flagged:   { cls: "bg-amber-50 border-amber-300 text-amber-700",       icon: <Flag className="w-3 h-3" />,        label: "Flagged" },
              na:        { cls: "bg-slate-50 border-slate-200 text-slate-400",       icon: <Minus className="w-3 h-3" />,       label: "N/A" },
            };
            const COMP_CAT_LABELS = { compliance: "Compliance", certifications: "Certifications", legal: "Legal" };

            if (complianceItems.length > 0) {
              const byCategory = complianceItems.reduce((acc, it) => {
                (acc[it.category] = acc[it.category] || []).push(it);
                return acc;
              }, {});
              return (
                <div className="space-y-4">
                  {Object.entries(byCategory).map(([cat, items]) => {
                    const addState = addCompState[cat] || { open: false, text: "" };
                    const done = items.filter(i => i.review_status !== "pending").length;
                    return (
                      <Card key={cat} className="border-[#E2E8F0]">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex items-center justify-between">
                            <span>{COMP_CAT_LABELS[cat] || cat} <span className="text-xs text-[#64748B] font-normal">({items.length})</span></span>
                            <span className={`text-xs font-normal ${done === items.length ? "text-emerald-600" : "text-[#64748B]"}`}>
                              {done}/{items.length} done
                            </span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-3">
                            {items.map((it) => {
                              const cfg = COMP_CFG[it.review_status] || COMP_CFG.pending;
                              const busy = compItemsBusy[it.id];
                              const noteOpen = compNoteState[it.id] !== undefined;
                              const noteVal = compNoteState[it.id]?.note ?? it.review_note ?? "";
                              const isNa = it.review_status === "na";
                              const editOpen = compEditState[it.id] !== undefined;
                              const editText = compEditState[it.id]?.text ?? it.text;
                              const editSaving = compEditState[it.id]?.saving;
                              return (
                                <li key={it.id} className={`space-y-1.5 ${isNa ? "opacity-50" : ""}`}>
                                  <div className="flex items-start gap-2">
                                    <span className={`shrink-0 mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium ${cfg.cls}`}>
                                      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : cfg.icon}
                                      {cfg.label}
                                    </span>
                                    <span className={`text-sm flex-1 ${isNa ? "line-through text-slate-400" : "text-[#0F172A]"}`}>{it.text}</span>
                                  </div>

                                  {/* inline edit form */}
                                  {editOpen && (
                                    <div className="pl-0.5 space-y-1.5">
                                      <Textarea
                                        value={editText}
                                        onChange={(e) => setCompEditState((p) => ({ ...p, [it.id]: { ...p[it.id], text: e.target.value } }))}
                                        rows={2} maxLength={600} className="text-xs" autoFocus
                                      />
                                      <div className="flex gap-1.5">
                                        <Button size="sm" className="h-6 text-xs" disabled={editSaving || !editText.trim()}
                                          onClick={() => handleSaveCompEdit(it)}>
                                          {editSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Save
                                        </Button>
                                        <Button size="sm" variant="ghost" className="h-6 text-xs" disabled={editSaving}
                                          onClick={() => setCompEditState((p) => { const n = {...p}; delete n[it.id]; return n; })}>
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  )}

                                  {/* action row */}
                                  {!editOpen && (
                                    <div className="flex items-center gap-1.5 pl-0.5 flex-wrap">
                                      {/* Compliant */}
                                      <button type="button" disabled={busy}
                                        onClick={() => it.review_status === "compliant"
                                          ? handleSetCompStatus(it, "pending", "")
                                          : handleSetCompStatus(it, "compliant")}
                                        className={`text-[11px] px-1.5 py-0.5 rounded border flex items-center gap-1 transition-colors disabled:opacity-50
                                          ${it.review_status === "compliant"
                                            ? COMP_CFG.compliant.cls + " font-medium"
                                            : "border-slate-200 text-slate-400 hover:border-slate-400 hover:text-slate-600"}`}>
                                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                                        Compliant
                                      </button>

                                      {/* Flagged */}
                                      <button type="button" disabled={busy}
                                        onClick={() => it.review_status === "flagged"
                                          ? handleSetCompStatus(it, "pending", "")
                                          : setCompNoteState((p) => ({ ...p, [it.id]: { note: it.review_note || "", pendingStatus: "flagged" } }))}
                                        className={`text-[11px] px-1.5 py-0.5 rounded border flex items-center gap-1 transition-colors disabled:opacity-50
                                          ${it.review_status === "flagged"
                                            ? COMP_CFG.flagged.cls + " font-medium"
                                            : "border-slate-200 text-slate-400 hover:border-slate-400 hover:text-slate-600"}`}>
                                        <Flag className="w-3 h-3" /> Flag
                                      </button>

                                      {/* N/A */}
                                      <button type="button" disabled={busy}
                                        onClick={() => handleSetCompStatus(it, isNa ? "pending" : "na", "")}
                                        className={`text-[11px] px-1.5 py-0.5 rounded border flex items-center gap-1 transition-colors disabled:opacity-50
                                          ${isNa
                                            ? COMP_CFG.na.cls + " font-medium"
                                            : "border-slate-200 text-slate-400 hover:border-slate-400 hover:text-slate-600"}`}>
                                        <Minus className="w-3 h-3" /> N/A
                                      </button>

                                      {/* Add note */}
                                      {it.review_status === "compliant" && !noteOpen && (
                                        <button type="button"
                                          onClick={() => setCompNoteState((p) => ({ ...p, [it.id]: { note: it.review_note || "", pendingStatus: "compliant" } }))}
                                          className="text-[11px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5">
                                          <MessageSquare className="w-3 h-3" />
                                          {it.review_note ? "Edit note" : "Add note"}
                                        </button>
                                      )}

                                      {/* Edit text (admin) */}
                                      {userIsAdmin && (
                                        <button type="button"
                                          onClick={() => setCompEditState((p) => ({ ...p, [it.id]: { text: it.text } }))}
                                          className="text-[11px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5">
                                          <Pencil className="w-3 h-3" /> Edit
                                        </button>
                                      )}

                                      {/* source section link */}
                                      {it.section_index != null && (
                                        <button type="button"
                                          onClick={() => {
                                            setActiveTab("sections");
                                            setTimeout(() => {
                                              document.querySelector(`[data-testid="section-${String(it.section_index)}"]`)
                                                ?.scrollIntoView({ behavior: "smooth", block: "start" });
                                            }, 150);
                                          }}
                                          className="text-[11px] text-blue-500 hover:text-blue-700 flex items-center gap-0.5 ml-auto">
                                          <ExternalLink className="w-3 h-3" />
                                          Source §{it.section_index + 1}
                                        </button>
                                      )}
                                    </div>
                                  )}

                                  {/* inline note form */}
                                  {noteOpen && (
                                    <div className="pl-0.5 space-y-1.5">
                                      <Textarea
                                        value={noteVal}
                                        onChange={(e) => setCompNoteState((p) => ({ ...p, [it.id]: { ...p[it.id], note: e.target.value } }))}
                                        placeholder={compNoteState[it.id]?.pendingStatus === "flagged" ? "Why flagged? (required)" : "Note (optional)"}
                                        rows={2} maxLength={600} className="text-xs" autoFocus
                                      />
                                      <div className="flex gap-1.5">
                                        <Button size="sm" className="h-6 text-xs"
                                          disabled={busy || (compNoteState[it.id]?.pendingStatus === "flagged" && !noteVal.trim())}
                                          onClick={() => handleSetCompStatus(it, compNoteState[it.id]?.pendingStatus || it.review_status, noteVal)}>
                                          {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Save
                                        </Button>
                                        <Button size="sm" variant="ghost" className="h-6 text-xs" disabled={busy}
                                          onClick={() => setCompNoteState((p) => { const n = {...p}; delete n[it.id]; return n; })}>
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  )}

                                  {/* existing note */}
                                  {!noteOpen && it.review_note && (
                                    <p className="pl-0.5 text-xs text-slate-500 bg-slate-50 rounded px-2 py-1 italic">{it.review_note}</p>
                                  )}

                                  {/* attribution */}
                                  {it.reviewer_name && (
                                    <p className="pl-0.5 text-[10px] text-slate-400">
                                      by {it.reviewer_name}
                                      {it.reviewed_at && <> · {new Date(it.reviewed_at).toLocaleDateString()}</>}
                                    </p>
                                  )}
                                </li>
                              );
                            })}
                          </ul>

                          {/* add compliance item */}
                          <div className="mt-3 pt-3 border-t border-[#E2E8F0]">
                            {!addState.open ? (
                              <button type="button"
                                onClick={() => setAddCompState((p) => ({ ...p, [cat]: { open: true, text: "" } }))}
                                className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1">
                                <Plus className="w-3 h-3" /> Add compliance item
                              </button>
                            ) : (
                              <div className="space-y-1.5">
                                <Textarea
                                  value={addState.text}
                                  onChange={(e) => setAddCompState((p) => ({ ...p, [cat]: { ...p[cat], text: e.target.value } }))}
                                  placeholder="Describe the compliance requirement…"
                                  rows={2} maxLength={600} className="text-xs"
                                />
                                <div className="flex gap-1.5">
                                  <Button size="sm" className="h-6 text-xs" disabled={!addState.text.trim() || addState.saving}
                                    onClick={() => handleAddCompItem(cat, addState.text)}>
                                    {addState.saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Add
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-6 text-xs"
                                    onClick={() => setAddCompState((p) => ({ ...p, [cat]: { open: false, text: "" } }))}>
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              );
            }

            // Fallback: raw JSON rendering when no DB items available
            const brief = rfp?.requirements_brief || {};
            const sectionItems = sections.flatMap((s) =>
              (s.compliance_items || []).map((item, idx) => ({
                ...item,
                sectionType: s.section_type,
                department: s.department,
                key: `${s.id}-${idx}`,
              }))
            );
            const briefItems = [
              ...(brief.compliance_items || []).map((s, i) => ({
                requirement: String(s),
                status: "review",
                sectionType: "compliance",
                department: "Legal",
                key: `brief-compliance-${i}`,
              })),
              ...(brief.certifications_required || []).map((s, i) => ({
                requirement: `Certification required: ${s}`,
                status: "review",
                sectionType: "eligibility",
                department: "Operations",
                key: `brief-cert-${i}`,
              })),
              ...(brief.legal_requirements || []).map((s, i) => ({
                requirement: String(s),
                status: "review",
                sectionType: "legal",
                department: "Legal",
                key: `brief-legal-${i}`,
              })),
            ];
            const items = [...sectionItems, ...briefItems];
            return (
          <Card className="border-[#E2E8F0]" data-testid="compliance-card">
            <CardHeader>
              <CardTitle className="font-['Manrope'] text-lg font-bold">
                Compliance & Eligibility Requirements
                <span className="ml-2 text-xs text-[#64748B] font-normal">
                  ({items.length} item{items.length === 1 ? "" : "s"})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {items.length === 0 ? (
                <div className="text-center py-8">
                  <ClipboardList className="w-12 h-12 mx-auto text-[#E2E8F0] mb-3" />
                  <p className="text-[#64748B] mb-3">
                    {isProcessing ? "Compliance items will appear after processing..." : "No compliance items detected"}
                  </p>
                  {!isProcessing && (
                    <button
                      type="button"
                      disabled={regeneratingCompliance}
                      onClick={async () => {
                        setRegeneratingCompliance(true);
                        try {
                          const brief = rfp?.requirements_brief || {};
                          const hasBrief = (brief.compliance_items || []).length || (brief.certifications_required || []).length || (brief.legal_requirements || []).length;
                          if (!hasBrief) {
                            await v2.post(`/rfp/${rfpId}/requirements-brief/regenerate`, null, { timeout: 180000 });
                          }
                          await v2.post(`/rfp/${rfpId}/compliance-items/sync`);
                          const items = await v2Api.briefItems.complianceItems(rfpId);
                          setComplianceItems(items);
                          toast.success("Compliance items synced");
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || err?.message || "Could not regenerate compliance items");
                        } finally {
                          setRegeneratingCompliance(false);
                        }
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                    >
                      {regeneratingCompliance ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : "Generate compliance items"}
                    </button>
                  )}
                </div>
              ) : (
                <ScrollArea className="h-[500px] pr-4">
                  <div className="space-y-3">
                    {items.map((item) => (
                      <div
                        key={item.key}
                        className="flex items-start gap-4 p-4 bg-[#F1F5F9] rounded-lg"
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          item.status === "met" ? "bg-emerald-100" :
                          item.status === "not_met" ? "bg-red-100" :
                          "bg-amber-100"
                        }`}>
                          {item.status === "met" ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          ) : item.status === "not_met" ? (
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                          ) : (
                            <Clock className="w-5 h-5 text-amber-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-[#0F172A]">{item.requirement}</p>
                          <div className="flex gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {item.sectionType?.replace('_', ' ')}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {item.department}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
            );
          })()}
          <TaggedSectionsPanel
            tagType="compliance"
            allLinks={allSectionLinks}
            sections={sections}
            onGoToSection={() => { setActiveTab("sections"); setActiveDepartment("all"); }}
          />
        </TabsContent>

        {/* Tables Tab — structured tables extracted by the tables_s
            pipeline stage. Each table renders as a real HTML table
            with the kind tag (spec / compliance / comparison / boq /
            priceschedule / manpower / deadline / eligibility / other). */}
        <TabsContent value="tables" className="space-y-4">
          {(() => {
            const tables = rfp?.tables || [];
            if (!tables.length) {
              return (
                <Card className="border-[#E2E8F0]">
                  <CardContent className="py-10 text-center text-[#64748B]">
                    <p className="text-base mb-2">No tables extracted for this RFP.</p>
                    <p className="text-sm mb-4">
                      The tables stage pulls spec / compliance / BoQ / price-schedule
                      tables from the document into structured rows + columns.
                    </p>
                    <button
                      type="button"
                      disabled={regeneratingTables}
                      onClick={async () => {
                        setRegeneratingTables(true);
                        try {
                          const r = await v2.post(`/rfp/${rfpId}/tables/regenerate`, null, { timeout: 180000 });
                          if (r?.data?.tables !== undefined) {
                            const rfpData = await v2Api.rfp.get(rfpId);
                            setRfp(adaptV2Rfp(rfpData));
                            toast.success(`${r.data.count || 0} table${r.data.count === 1 ? "" : "s"} extracted`);
                          }
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || err?.message || "Could not extract tables");
                        } finally {
                          setRegeneratingTables(false);
                        }
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                    >
                      {regeneratingTables ? <><Loader2 className="w-4 h-4 animate-spin" /> Extracting…</> : "Extract tables"}
                    </button>
                  </CardContent>
                </Card>
              );
            }
            const kindColor = {
              spec: "bg-indigo-100 text-indigo-700",
              compliance: "bg-emerald-100 text-emerald-700",
              comparison: "bg-purple-100 text-purple-700",
              boq: "bg-blue-100 text-blue-700",
              priceschedule: "bg-amber-100 text-amber-700",
              manpower: "bg-rose-100 text-rose-700",
              deadline: "bg-red-100 text-red-700",
              eligibility: "bg-slate-200 text-slate-800",
              other: "bg-slate-100 text-slate-700",
            };
            return (
              <div className="space-y-4">
                {tables.map((t, i) => (
                  <Card key={i} className="border-[#E2E8F0]">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between gap-3">
                        <CardTitle className="text-base">{t.title || `Table ${i + 1}`}</CardTitle>
                        <div className="flex items-center gap-2 text-xs text-[#64748B]">
                          <Badge className={`text-xs ${kindColor[t.kind] || kindColor.other}`}>
                            {t.kind || "other"}
                          </Badge>
                          <span>{(t.rows || []).length} row{(t.rows || []).length === 1 ? "" : "s"}</span>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="border-b border-slate-200 bg-slate-50">
                              {(t.headers || []).map((h, hi) => (
                                <th key={hi} className="text-left py-2 px-3 font-medium text-slate-700">
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(t.rows || []).map((row, ri) => (
                              <tr key={ri} className="border-b border-slate-100 hover:bg-slate-50">
                                {(row || []).map((cell, ci) => (
                                  <td key={ci} className="py-2 px-3 align-top text-slate-800">
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            );
          })()}
        </TabsContent>

        {/* Diagrams Tab */}
        <TabsContent value="diagrams" className="space-y-4">
          <DiagramsPanel rfpId={rfpId} />
        </TabsContent>

        {/* Deadlines Tab */}
        <TabsContent value="deadlines" className="space-y-6">
          <Card className="border-[#E2E8F0]" data-testid="deadlines-card">
            <CardHeader>
              <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#FF3B30]" />
                Key Deadlines
              </CardTitle>
            </CardHeader>
            <CardContent>
              {rfp.key_deadlines?.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 mx-auto text-[#E2E8F0] mb-3" />
                  <p className="text-[#64748B]">
                    {isProcessing ? "Deadlines will appear after processing..." : "No deadlines detected"}
                  </p>
                </div>
              ) : (() => {
                const activeDeadlines = (rfp.key_deadlines || []).map((d, i) => ({ ...d, _idx: i })).filter((d) => !d.ignored);
                const ignoredDeadlines = (rfp.key_deadlines || []).map((d, i) => ({ ...d, _idx: i })).filter((d) => d.ignored);
                const renderDeadline = (deadline) => {
                  const idx = deadline._idx;
                  const isEditing = deadlineEditState[idx] !== undefined;
                  const editDate = deadlineEditState[idx]?.date ?? deadline.date;
                  const editContext = deadlineEditState[idx]?.context ?? deadline.context;
                  const editSaving = deadlineEditState[idx]?.saving;
                  return (
                    <div key={idx} className={`p-4 border rounded-lg ${deadline.ignored ? "bg-slate-50 border-slate-100 opacity-60" : "bg-red-50 border-red-100"}`}>
                      {isEditing ? (
                        <div className="space-y-2">
                          <Input value={editDate}
                            onChange={(e) => setDeadlineEditState((p) => ({ ...p, [idx]: { ...p[idx], date: e.target.value } }))}
                            placeholder="Date" className="h-8 text-sm font-mono" />
                          <Textarea value={editContext}
                            onChange={(e) => setDeadlineEditState((p) => ({ ...p, [idx]: { ...p[idx], context: e.target.value } }))}
                            rows={2} maxLength={500} className="text-sm" />
                          <div className="flex gap-2">
                            <Button size="sm" className="h-7 text-xs" disabled={editSaving || !editDate.trim() || !editContext.trim()}
                              onClick={() => handlePatchDeadline(idx, { date: editDate, context: editContext })}>
                              {editSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Save
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs"
                              onClick={() => setDeadlineEditState((p) => { const n = {...p}; delete n[idx]; return n; })}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center shrink-0">
                            <Calendar className="w-5 h-5 text-[#FF3B30]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-mono font-semibold text-sm ${deadline.ignored ? "line-through text-slate-400" : "text-[#FF3B30]"}`}>
                              {deadline.date}
                            </p>
                            <p className="text-sm text-[#0F172A] mt-0.5">{deadline.context}</p>
                          </div>
                          {userIsAdmin && (
                            <div className="flex items-center gap-1 shrink-0">
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-slate-700"
                                onClick={() => setDeadlineEditState((p) => ({ ...p, [idx]: { date: deadline.date, context: deadline.context } }))}>
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="icon" variant="ghost"
                                className={`h-7 w-7 ${deadline.ignored ? "text-slate-500 hover:text-slate-700" : "text-slate-400 hover:text-amber-600"}`}
                                title={deadline.ignored ? "Restore" : "Ignore"}
                                onClick={() => handlePatchDeadline(idx, { ignored: !deadline.ignored })}>
                                {deadline.ignored ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                };
                return (
                  <div className="space-y-3">
                    {activeDeadlines.map(renderDeadline)}
                    {ignoredDeadlines.length > 0 && (
                      <div>
                        <button type="button"
                          onClick={() => setShowIgnoredDeadlines((v) => !v)}
                          className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 mt-1">
                          <ChevronDown className={`w-3 h-3 transition-transform ${showIgnoredDeadlines ? "rotate-180" : ""}`} />
                          {showIgnoredDeadlines ? "Hide" : "Show"} {ignoredDeadlines.length} ignored
                        </button>
                        {showIgnoredDeadlines && (
                          <div className="space-y-2 mt-2">
                            {ignoredDeadlines.map(renderDeadline)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Add deadline form */}
              <div className="mt-4 border-t pt-4">
                {!addDeadlineState.open ? (
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                    onClick={() => setAddDeadlineState((p) => ({ ...p, open: true }))}>
                    <Plus className="w-3 h-3" /> Add deadline
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Input
                      placeholder="Date (e.g. 30 July 2025)"
                      value={addDeadlineState.date}
                      onChange={(e) => setAddDeadlineState((p) => ({ ...p, date: e.target.value }))}
                      className="h-8 text-sm"
                    />
                    <Textarea
                      placeholder="Context / description…"
                      value={addDeadlineState.context}
                      onChange={(e) => setAddDeadlineState((p) => ({ ...p, context: e.target.value }))}
                      rows={2}
                      maxLength={500}
                    />
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="ghost" className="h-7 text-xs"
                        disabled={addDeadlineState.saving}
                        onClick={() => setAddDeadlineState({ open: false, date: "", context: "", saving: false })}>
                        Cancel
                      </Button>
                      <Button size="sm" className="h-7 text-xs"
                        disabled={addDeadlineState.saving || !addDeadlineState.date.trim() || !addDeadlineState.context.trim()}
                        onClick={handleAddDeadline}>
                        {addDeadlineState.saving && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                        Add
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          <TaggedSectionsPanel
            tagType="deadline"
            allLinks={allSectionLinks}
            sections={sections}
            onGoToSection={() => { setActiveTab("sections"); setActiveDepartment("all"); }}
          />
        </TabsContent>

        {/* BOQ Tab — editable BoQ grid + sections tagged as BOQ by reviewers */}
        <TabsContent value="boq" className="space-y-6">
          {/* Editable Bill of Quantities */}
          <Card className="border-[#E2E8F0]">
            <CardHeader>
              <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-[#007AFF]" />
                Bill of Quantities
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <RfpBoqTab rfp={rfp} />
            </CardContent>
          </Card>

          {/* Sections reviewers tagged as BOQ-relevant */}
          <div>
            <h3 className="text-sm font-semibold text-[#64748B] uppercase tracking-wide mb-3 px-1">
              Tagged Source Sections
            </h3>
            <TaggedSectionsPanel
              tagType="boq_note"
              allLinks={allSectionLinks}
              sections={sections}
              onGoToSection={() => { setActiveTab("sections"); setActiveDepartment("all"); }}
            />
            {allSectionLinks.filter((l) => l.entity_type === "boq_note").length === 0 && (
              <Card className="border-[#E2E8F0]">
                <CardContent className="py-8 text-center text-[#64748B]">
                  <p className="text-sm">No sections tagged as BOQ yet.</p>
                  <p className="text-xs mt-1 text-slate-400">
                    In the Sections tab, click the <strong>BOQ</strong> tag chip on any section to surface it here.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

      </Tabs>

      {readerSection && (
        <SectionOriginalReader
          rfpId={rfpId}
          open={!!readerSection}
          onOpenChange={(open) => !open && setReaderSection(null)}
          pageStart={(readerSection.page_start ?? 0) + 1}
          pageEnd={(readerSection.page_end ?? readerSection.page_start ?? 0) + 1}
          sectionTitle={readerSection.title}
        />
      )}

      {/* "View on PDF" popup — highlighted page viewer */}
      <Dialog open={!!pdfDialogSection} onOpenChange={(open) => !open && setPdfDialogSection(null)}>
        <DialogContent className="max-w-3xl w-full p-0 overflow-hidden"
          style={{ height: "90vh" }}>
          <DialogHeader className="px-4 pt-4 pb-0 shrink-0">
            <DialogTitle className="text-sm font-semibold text-slate-700 truncate">
              {pdfDialogSection?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden px-4 pb-4 pt-2" style={{ height: "calc(90vh - 60px)" }}>
            <SectionPdfPane rfpId={rfpId} section={pdfDialogSection} />
          </div>
        </DialogContent>
      </Dialog>

      {exemplarsSection && (
        <SectionExemplarsSheet
          open={!!exemplarsSection}
          onOpenChange={(open) => !open && setExemplarsSection(null)}
          section={exemplarsSection}
        />
      )}

      {crossValSection && (
        <SectionCrossValidationSheet
          open={!!crossValSection}
          onOpenChange={(open) => !open && setCrossValSection(null)}
          rfpId={rfpId}
          sectionIndex={crossValSection.section_index ?? null}
          sectionTitle={crossValSection.title}
        />
      )}
    </div>
  );
};

export default RFPAnalysis;
